import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { FaEdit, FaPaperclip, FaTrash, FaVideo } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/css/GuestMaster.css";
import function1 from "../../assets/images/function1.svg";
import { getMainGuestsAndAttendees } from "../../utils/getGuestWithAttendees";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  getOccasionsByEventId,
  sendInvites,
  getSentInvites,
  updateSentInvite,
  FRONTEND_URL,
  sendEmailInvite,
  previewEmailInvite,
  sendWhatsAppInvite,
  publishWhatsAppEventPass,
  getGuestAccomodation,
  getAllTravel,
  getSeatAllocation,
  getHotelsByEventId,
  getTrackedInvites,
} from "../../server/Api";
import axios from "axios";
import { canEditPage } from "../../utils/pagePermissions";
import { getLookupUtils } from "../../utils/lookup";
import { fetchAllocations } from "../../utils/getAllocatedGuestAndAttendees";
import * as XLSX from "sheetjs-style";
import { getOccasionThemeForId } from "../../utils/allocateQrThemes";
import { blobToDataUrl } from "../../utils/eventPassQr";
import {
  blobToBase64,
  generateInvitationCardBlob,
} from "../../utils/invitationCard";

const QR_MODE_SAME = "same";
const QR_MODE_PER_OCCASION = "per-occasion";

const INVITE_KINDS = [
  {
    id: "invitation",
    title: "Invitation",
    subtitle: "Formal event invitation by email or other channels",
    detail: "Choose occasion and invitation type",
    icon: "bi-envelope-heart",
  },
  {
    id: "accommodation",
    title: "Accommodation",
    subtitle: "Intimation for guests with stay arrangements",
    detail: "Choose invitation type only",
    icon: "bi-building",
  },
  {
    id: "travel",
    title: "Travel",
    subtitle: "Intimation for guests with travel arrangements",
    detail: "Choose invitation type only",
    icon: "bi-airplane",
  },
  {
    id: "seat",
    title: "Seat Allocation",
    subtitle: "Intimation for guests with assigned seats",
    detail: "Choose occasion and invitation type",
    icon: "bi-grid-3x3-gap",
  },
  {
    id: "itinerary",
    title: "Itinerary",
    subtitle: "Share personal itinerary link with confirmed guests",
    detail: "RSVP Yes guests with a registration number",
    icon: "bi-map",
  },
];

const getAppBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return String(window.location.origin).replace(/\/$/, "");
  }
  return String(FRONTEND_URL || "").replace(/\/$/, "");
};

/** Public shape only — real opaque token is minted on the server when sending/previewing. */
const buildGuestItineraryUrl = (eventId, regNo) => {
  const cleanReg = String(regNo || "").trim();
  if (!eventId || !cleanReg) return "";
  // Placeholder for editor/preview tokens; backend replaces with ?t=<opaque>
  return `${getAppBaseUrl()}/guest-itinerary?t=`;
};

const readEventQrMode = (eventId) => {
  try {
    const saved = localStorage.getItem(
      `allocate-qr-mode:${eventId || "default"}`
    );
    return saved === QR_MODE_PER_OCCASION
      ? QR_MODE_PER_OCCASION
      : QR_MODE_SAME;
  } catch {
    return QR_MODE_SAME;
  }
};

const applyTokenPlaceholders = (text, values = {}) => {
  let result = String(text || "");
  Object.entries(values).forEach(([key, value]) => {
    if (!key) return;
    const safeKey = String(key).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\{\\{\\s*${safeKey}\\s*\\}\\}`, "gi");
    result = result.replace(re, value == null ? "" : String(value));
  });
  return result;
};

const FIELD_LABELS = {
  guestName: "Guest Name",
  prefix: "Prefix",
  firstName: "First Name",
  middleName: "Middle Name",
  lastName: "Last Name",
  email: "Email",
  mobile: "Mobile",
  regNo: "Registration No",
  occasionName: "Occasion Name",
  eventName: "Event Name",
  itineraryUrl: "Itinerary URL",
  details: "All Assignment Details",
  hotelName: "Hotel Name",
  hotelAddress: "Hotel Address",
  roomType: "Room Type",
  checkInDate: "Check-in Date",
  checkInTime: "Check-in Time",
  checkOutDate: "Check-out Date",
  checkOutTime: "Check-out Time",
  noOfRooms: "No. of Rooms",
  occupancy: "Occupancy",
  stayStatus: "Stay Status",
  floorPreference: "Floor Preference",
  roomFacing: "Room Facing",
  roomPreference: "Room Preference",
  specialRequirements: "Special Requirements",
  arrivalDate: "Arrival Date",
  arrivalTime: "Arrival Time",
  arrivalMode: "Arrival Mode",
  arrivalFlightTrainBusNo: "Arrival Flight/Train/Bus",
  arrivalPNRNo: "Arrival PNR",
  arrivalPickUpLocation: "Arrival Pickup Location",
  pickupRequired: "Pickup Required",
  pickupLocation: "Pickup Location",
  pickupDate: "Pickup Date",
  pickupTime: "Pickup Time",
  pickupVehicle: "Pickup Vehicle",
  pickupChauffeur: "Pickup Chauffeur",
  pickupDriverMobile: "Pickup Driver Mobile",
  pickupInstructions: "Pickup Instructions",
  departureDate: "Departure Date",
  departureTime: "Departure Time",
  departureMode: "Departure Mode",
  departureFlightTrainBusNo: "Departure Flight/Train/Bus",
  departurePNRNo: "Departure PNR",
  departureDropLocation: "Departure Drop Location",
  dropRequired: "Drop Required",
  dropLocation: "Drop Location",
  dropDate: "Drop Date",
  dropTime: "Drop Time",
  dropVehicle: "Drop Vehicle",
  dropChauffeur: "Drop Chauffeur",
  dropDriverMobile: "Drop Driver Mobile",
  dropInstructions: "Drop Instructions",
  localVehicle: "Local Vehicle",
  itineraryNotes: "Itinerary Notes",
  seatLabel: "Seat Allocation",
  tableNumber: "Table Number",
  rowNumber: "Row Number",
  seatNumber: "Seat Number",
};

const detailLine = (label, value) => {
  if (value === undefined || value === null || value === "") return null;
  return `${label}: ${value}`;
};

const formatSeatLabel = (seat) => {
  if (!seat) return "";
  const parts = [];
  if (seat.tableAllocation?.tableNumber != null) {
    parts.push(`Table ${seat.tableAllocation.tableNumber}`);
    if (seat.tableAllocation.seatNumber != null) {
      parts.push(`Seat ${seat.tableAllocation.seatNumber}`);
    }
  }
  if (seat.rowAllocation?.rowNumber != null) {
    parts.push(`Row ${seat.rowAllocation.rowNumber}`);
    if (seat.rowAllocation.seatNumber != null) {
      parts.push(`Seat ${seat.rowAllocation.seatNumber}`);
    }
  }
  if (!parts.length && seat.remarks) parts.push(seat.remarks);
  return parts.join(", ");
};

const formatAddressValue = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address.trim();
  if (typeof address !== "object") return "";
  return [
    address.line1,
    address.line2,
    address.line3,
    address.city,
    address.state,
    address.pincode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const formatWhatsAppDateTime = (dateValue, timeValue = "") => {
  const datePart = String(dateValue || "").trim();
  const timePart = String(timeValue || "").trim();
  if (!datePart && !timePart) return "";
  let prettyDate = datePart;
  if (datePart) {
    const parsed = new Date(String(datePart).split("T")[0]);
    if (!Number.isNaN(parsed.getTime())) {
      prettyDate = parsed.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  }
  return [prettyDate, timePart].filter(Boolean).join(", ");
};

const formatVenueFromOccasion = (occasion) => {
  if (!occasion) return "";
  const venueName = occasion.venueAddress?.name || "";
  const venueAddr = formatAddressValue(occasion.venueAddress?.address);
  return [venueName, venueAddr].filter(Boolean).join(", ");
};

const formatInvitationOccasionDetails = (occasionsList = []) => {
  const items = (occasionsList || []).filter(Boolean);
  const names = items
    .map((o) => String(o.OccasionName || "").trim())
    .filter(Boolean);
  const dateTimes = items
    .map((o) => {
      const dt = formatWhatsAppDateTime(o.Date, o.time);
      if (!dt) return "";
      return items.length > 1 && o.OccasionName
        ? `${o.OccasionName}: ${dt}`
        : dt;
    })
    .filter(Boolean);
  const venues = items
    .map((o) => formatVenueFromOccasion(o))
    .filter(Boolean);
  const uniqueVenues = [...new Set(venues)];
  const venueLine =
    uniqueVenues.length <= 1
      ? uniqueVenues[0] || ""
      : items
          .map((o) => {
            const venue = formatVenueFromOccasion(o);
            if (!venue) return "";
            return o.OccasionName ? `${o.OccasionName}: ${venue}` : venue;
          })
          .filter(Boolean)
          .join(" | ");
  return {
    occasionNames: names.join(", "),
    dateTime: dateTimes.join(" | "),
    venue: venueLine,
  };
};

const formatHostContact = (occasion, event) => {
  const host = occasion?.hostname || {};
  const hostName = [host.prefix, host.firstName, host.lastName, host.suffix]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (host.mobileNo) {
    return hostName ? `${hostName}, ${host.mobileNo}` : String(host.mobileNo);
  }
  if (hostName) return hostName;
  return String(event?.hostedBy || event?.eventName || "the event team");
};

const isWhatsAppInvitationType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase() === "whatsapp";

const WHATSAPP_TEMPLATE_BY_KIND = {
  invitation: "festiva_rsvp",
  accommodation: "festiva_accommodation",
  travel: "festiva_travel",
  seat: "festiva_seat_allocation",
  itinerary: "festiva_guest_itinerary_2",
};

const getWhatsAppAppBaseUrl = () =>
  String(
    process.env.REACT_APP_FRONTEND_URL ||
      (typeof window !== "undefined" ? window.location.origin : "") ||
      ""
  ).replace(/\/$/, "");

const waPreviewText = (value, fallback = "To be confirmed") => {
  const cleaned = String(value ?? "").trim();
  return cleaned || fallback;
};

const buildWhatsAppPreviewBodyText = (kind, bodyValues = []) => {
  const v = (i, fallback = "") => String(bodyValues[i] ?? fallback);
  if (kind === "invitation") {
    return [
      `Dear ${v(0, "Guest")},`,
      "",
      `You are cordially invited to ${v(1, "our celebration")} for ${v(2, "the occasion")}.`,
      "",
      `Date & Time: ${v(3, "To be confirmed")}`,
      `Venue: ${v(4, "To be confirmed")}`,
      "",
      String(v(2, "")).includes(",")
        ? "Please confirm your attendance using the button below. Your RSVP applies to all listed occasions."
        : "Please refer to the invitation above for the event details and confirm your attendance using the button below.",
      "",
      "We look forward to celebrating with you.",
    ].join("\n");
  }
  if (kind === "accommodation") {
    return [
      `Dear ${v(0, "Guest")},`,
      "",
      `Your stay for ${v(1, "our celebration")} is confirmed.`,
      "",
      `Hotel: ${v(2, "Hotel")}`,
      `Address: ${v(3, "To be confirmed")}`,
      `Check-in: ${v(4, "To be confirmed")}`,
      `Check-out: ${v(5, "To be confirmed")}`,
      `Room: ${v(6, "To be confirmed")}`,
      "",
      `For assistance, contact ${v(7, "the event team")}.`,
    ].join("\n");
  }
  if (kind === "travel") {
    return [
      `Dear ${v(0, "Guest")},`,
      "",
      `Your ${v(1, "Pickup")} for ${v(2, "our celebration")} is arranged.`,
      "",
      `Date & Time: ${v(3, "To be confirmed")}`,
      `Location: ${v(4, "To be confirmed")}`,
      `Vehicle: ${v(5, "To be confirmed")}`,
      `Driver: ${v(6, "To be confirmed")}`,
      "",
      `For help, contact ${v(7, "the event team")}.`,
    ].join("\n");
  }
  if (kind === "seat") {
    return [
      `Dear ${v(0, "Guest")},`,
      "",
      `Your seating for ${v(1, "our celebration")} — ${v(2, "the occasion")}:`,
      "",
      `Seat: ${v(3, "Assigned")}`,
      "",
      `For help, contact ${v(4, "the event team")}.`,
    ].join("\n");
  }
  if (kind === "itinerary") {
    return [
      `Dear ${v(0, "Guest")},`,
      "",
      `Thank you for confirming your attendance at ${v(1, "our celebration")}.`,
      "",
      `Your personalized itinerary includes ${v(2, "your event details")}.`,
      "",
      "Tap below to view your full itinerary.",
    ].join("\n");
  }
  return "";
};

/** Instant local preview from Interakt template shape + guest/event values. */
const buildLocalWhatsAppPreview = (payload = {}) => {
  const kind = String(payload.inviteKind || "invitation").toLowerCase();
  const templateName = WHATSAPP_TEMPLATE_BY_KIND[kind] || kind;
  const appBase = getWhatsAppAppBaseUrl();
  const sampleToken = "sampletoken";

  if (kind === "travel" && Array.isArray(payload.trips) && payload.trips.length) {
    const bubbles = payload.trips.map((trip) => {
      const bodyValues = [
        waPreviewText(payload.guestName, "Guest"),
        waPreviewText(trip.tripType, "Pickup"),
        waPreviewText(payload.eventName, "our celebration"),
        waPreviewText(trip.dateTime),
        waPreviewText(trip.location),
        waPreviewText(trip.vehicle),
        waPreviewText(trip.driver),
        waPreviewText(payload.hostTeam, "the event team"),
      ];
      return {
        tripType: trip.tripType || "",
        bodyText: buildWhatsAppPreviewBodyText("travel", bodyValues),
      };
    });
    return {
      inviteKind: kind,
      templateName,
      previews: [
        {
          templateName,
          headerMediaUrl: "",
          buttons: [],
          bodyText: bubbles[0]?.bodyText || "",
          bubbles,
        },
      ],
    };
  }

  let bodyValues = [];
  let headerMediaUrl = "";
  let buttons = [];

  if (kind === "invitation") {
    bodyValues = [
      waPreviewText(payload.guestName, "Guest"),
      waPreviewText(payload.eventName, "our celebration"),
      waPreviewText(payload.occasionNames, "the occasion"),
      waPreviewText(payload.dateTime),
      waPreviewText(payload.venue),
    ];
    headerMediaUrl = String(payload.headerMediaUrl || "").trim();
    buttons = [
      {
        label: "Yes, I'll Attend",
        url: `${appBase}/guest-rsvp?r=y&t=${sampleToken}`,
      },
      {
        label: "Sorry, Can't Make It",
        url: `${appBase}/guest-rsvp?r=n&t=${sampleToken}`,
      },
    ];
  } else if (kind === "accommodation") {
    bodyValues = [
      waPreviewText(payload.guestName, "Guest"),
      waPreviewText(payload.eventName, "our celebration"),
      waPreviewText(payload.hotelName, "Hotel"),
      waPreviewText(payload.hotelAddress),
      waPreviewText(payload.checkIn),
      waPreviewText(payload.checkOut),
      waPreviewText(payload.roomLabel),
      waPreviewText(
        payload.contactPerson,
        waPreviewText(payload.contactMobile, "the event team")
      ),
    ];
  } else if (kind === "seat") {
    bodyValues = [
      waPreviewText(payload.guestName, "Guest"),
      waPreviewText(payload.eventName, "our celebration"),
      waPreviewText(payload.occasionName, "the occasion"),
      waPreviewText(payload.seatLabel, "Assigned"),
      waPreviewText(payload.hostTeam, "the event team"),
    ];
  } else if (kind === "itinerary") {
    bodyValues = [
      waPreviewText(payload.guestName, "Guest"),
      waPreviewText(payload.eventName, "our celebration"),
      waPreviewText(
        payload.itineraryDetails,
        "your event schedule, accommodation, travel arrangements, and seating information"
      ),
    ];
    buttons = [
      { label: "Visit Website", url: appBase || "#" },
      {
        label: "View Itinerary",
        url: `${appBase}/guest-itinerary?t=${sampleToken}`,
      },
    ];
  } else {
    bodyValues = [
      waPreviewText(payload.guestName, "Guest"),
      waPreviewText(payload.eventName, "our celebration"),
    ];
  }

  const preview = {
    templateName,
    bodyText: buildWhatsAppPreviewBodyText(kind, bodyValues),
    headerMediaUrl,
    buttons,
  };

  return {
    inviteKind: kind,
    templateName,
    previews: [preview],
  };
};

const EMPTY_ASSIGNMENT_COPY = {
  accommodation: {
    title: "No accommodation assigned",
    body: "Assign stays under Travel & Stay before sending accommodation intimations.",
    actionLabel: "Go to Travel & Stay",
    actionPath: "/travel-and-stay",
  },
  travel: {
    title: "No travel assigned",
    body: "Add arrival/departure travel records under Travel & Stay before sending travel intimations.",
    actionLabel: "Go to Travel & Stay",
    actionPath: "/travel-and-stay",
  },
  seat: {
    title: "No seat allocation found",
    body: "Allocate seats for the selected occasion before sending seat intimations.",
    actionLabel: "Go to Seat Allocation",
    actionPath: "/seat-allocation/board",
  },
  itinerary: {
    title: "No confirmed guests found",
    body: "Itinerary links can be shared only after guests RSVP Yes (and have a registration number).",
    actionLabel: "Go to RSVP",
    actionPath: "/track-invites",
  },
};

const SendInvites = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [guestWithAttendees, setGuestWithAttendees] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const eventEntity = useSelector((state) => state.auth.user);
  const canEdit = canEditPage(selectedEvent, "send-invites", eventEntity);
  const [selectedAllocations, setSelectedAllocations] = useState({});
  const [regNoMap, setRegNoMap] = useState({});
  const navigate = useNavigate();
  const [invitationDropdown, setInvitationDropdown] = useState([]);
  const [savedInvites, setSavedInvites] = useState([]);
  const [editableInvites, setEditableInvites] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const [editMode, setEditMode] = useState({});
  const [emailModal, setEmailModal] = useState({
    isOpen: false,
    subject: "",
    content: "",
    footer: "Warm regards,\nTecogis Team", // default footer
    attachMedia: false,
    addQR: false,
  });
  const [emailPreviewHtml, setEmailPreviewHtml] = useState("");
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreviewDevice, setEmailPreviewDevice] = useState("desktop");
  const [previewMailOccasionId, setPreviewMailOccasionId] = useState("");
  const [emailInviteCardUrl, setEmailInviteCardUrl] = useState("");
  const [emailDeviceFiles, setEmailDeviceFiles] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const emailFileInputRef = useRef(null);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappPreviewGuestIndex, setWhatsappPreviewGuestIndex] = useState(0);
  const [previewWhatsAppOccasionId, setPreviewWhatsAppOccasionId] =
    useState("");
  const [whatsappEventPassUrl, setWhatsappEventPassUrl] = useState("");
  const whatsappPassCacheRef = useRef({});
  const [dynamicFieldTarget, setDynamicFieldTarget] = useState("content");
  const emailPreviewHtmlRef = useRef("");
  const subjectInputRef = useRef(null);
  const contentInputRef = useRef(null);
  const [formData, setFormData] = useState({
    occasionId: "all",
    invitationType: "",
  });
  const [inviteKind, setInviteKind] = useState(null);
  const [eligiblePersonKeys, setEligiblePersonKeys] = useState(null);
  const [assignmentRecords, setAssignmentRecords] = useState({
    accommodations: [],
    hotelsById: {},
    travels: [],
    seatDocs: [],
    loaded: false,
  });
  const [lookupInvitationTypes, setLookupInvitationTypes] = useState([]);
  const [qrMode, setQrMode] = useState(() =>
    readEventQrMode(selectedEvent?.eventId)
  );

  // Itinerary is whole-event (wedding-level) — no occasion picker
  const needsOccasion =
    inviteKind === "invitation" || inviteKind === "seat";
  const isPerOccasionQr =
    inviteKind === "invitation" && qrMode === QR_MODE_PER_OCCASION;
  // Occasion tabs + one message per occasion only when each occasion has its own QR.
  const splitInviteByOccasion =
    inviteKind === "invitation" &&
    formData.occasionId === "all" &&
    isPerOccasionQr;
  const hasNoAssignments =
    inviteKind &&
    inviteKind !== "invitation" &&
    assignmentRecords.loaded &&
    eligiblePersonKeys instanceof Set &&
    eligiblePersonKeys.size === 0;
  const emptyAssignmentCopy =
    EMPTY_ASSIGNMENT_COPY[inviteKind] || EMPTY_ASSIGNMENT_COPY.accommodation;

  useEffect(() => {
    setQrMode(readEventQrMode(selectedEvent?.eventId));
  }, [selectedEvent?.eventId]);

  const syncInvitationDropdown = useCallback(
    (selectedOccasionId) => {
      let updatedInvitationDropdown = [];

      if (!needsOccasion || selectedOccasionId === "all") {
        if (lookupInvitationTypes.length > 0) {
          updatedInvitationDropdown = lookupInvitationTypes.map(
            (item) => item.Lookup_Value_Code || item._id
          );
        } else if (occasions.length > 0) {
          const allTypes = occasions.flatMap((o) => o.InvitationType || []);
          updatedInvitationDropdown = [...new Set(allTypes)];
        }
      } else {
        const selectedOccasion = occasions.find(
          (item) => item._id === selectedOccasionId
        );
        updatedInvitationDropdown = selectedOccasion?.InvitationType || [];
      }

      // Itinerary is digital-only: Email + WhatsApp
      if (inviteKind === "itinerary") {
        const allowed = new Set(["email", "whatsapp"]);
        updatedInvitationDropdown = updatedInvitationDropdown.filter((type) =>
          allowed.has(String(type || "").trim().toLowerCase())
        );
        if (updatedInvitationDropdown.length === 0) {
          updatedInvitationDropdown = ["Email", "WhatsApp"];
        }
      }

      setInvitationDropdown(
        updatedInvitationDropdown.map((type) => ({
          _id: type,
          Lookup_Value_Code: type,
          Lookup_Value_Name: type,
        }))
      );
    },
    [needsOccasion, lookupInvitationTypes, occasions, inviteKind]
  );

  const handleSelectInviteKind = (kind) => {
    setQrMode(readEventQrMode(selectedEvent?.eventId));
    setInviteKind(kind);
    setFormData({
      occasionId: "all",
      invitationType: "",
    });
    setSelectedRows({});
    setSearchTerm("");
    setEligiblePersonKeys(null);
  };

  const handleBackToInviteKinds = () => {
    setInviteKind(null);
    setFormData({ occasionId: "all", invitationType: "" });
    setSelectedRows({});
    setEligiblePersonKeys(null);
  };

  const handleEditClick = (key) => {
    setEditMode((prev) => ({
      ...prev,
      [key]: true,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const fetchSavedInvites = async () => {
    if (!selectedEvent?.eventId) return;

    try {
      const res = await axios.get(getSentInvites(selectedEvent.eventId));
      setSavedInvites(res.data.data || []);
      console.log("Fetched savedInvites:", res.data.data);
    } catch (error) {
      console.error("Failed to fetch savedInvites:", error);
      toast.error("Failed to load saved invites.");
    }
  };

  // run once on mount/eventId change
  useEffect(() => {
    fetchSavedInvites();
  }, [selectedEvent?.eventId]);

  // Fetch occasions
useEffect(() => {
  const fetchOccasions = async () => {
    try {
      if (!selectedEvent?.eventId) return;
      const url = getOccasionsByEventId(selectedEvent.eventId);
      const res = await axios.get(url); // Make the request
      // Filter only active occasions
      const activeOccasions = (res.data.data || []).filter(
        (occasion) => occasion.isOccasionActive
      );
      setOccasions(activeOccasions);
    } catch (error) {
      console.error("Failed to fetch occasions", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load occasions");
      }
      setOccasions([]);
    }
  };

  fetchOccasions();
}, [selectedEvent]);


  useEffect(() => {
    const fetchDropdownData = async (lookupId, setDropdown, errorMessage) => {
      try {
        const result = await getLookupUtils(lookupId);
        setDropdown(result.data);

        // Log gift data if lookupId is "18"
        if (lookupId === "18") {
          console.log("Gift data fetched:", result.data);
        }
      } catch (error) {
        console.error(errorMessage, error);
      }
    };

    fetchDropdownData(
      "6",
      (data) => {
        setLookupInvitationTypes(data || []);
        setInvitationDropdown(data || []);
      },
      "Failed to fetch Invitation Type:"
    );
  }, []);

  useEffect(() => {
    if (!inviteKind) return;
    syncInvitationDropdown(formData.occasionId);
  }, [inviteKind, formData.occasionId, syncInvitationDropdown]);

  // Load eligibility + detail records for accommodation / travel / seat
  useEffect(() => {
    const loadEligibility = async () => {
      if (!selectedEvent?.eventId || !inviteKind) {
        setEligiblePersonKeys(null);
        setAssignmentRecords({
          accommodations: [],
          hotelsById: {},
          travels: [],
          seatDocs: [],
          loaded: false,
        });
        return;
      }

      if (inviteKind === "invitation") {
        setEligiblePersonKeys(null);
        setAssignmentRecords({
          accommodations: [],
          hotelsById: {},
          travels: [],
          seatDocs: [],
          loaded: true,
        });
        return;
      }

      setAssignmentRecords((prev) => ({ ...prev, loaded: false }));

      try {
        if (inviteKind === "accommodation") {
          const [accRes, hotelRes] = await Promise.all([
            axios.get(getGuestAccomodation(selectedEvent.eventId)),
            axios.get(getHotelsByEventId(selectedEvent.eventId)),
          ]);
          const rows = accRes.data?.data || accRes.data || [];
          const hotels = hotelRes.data?.data || hotelRes.data || [];
          const hotelsById = {};
          (Array.isArray(hotels) ? hotels : []).forEach((hotel) => {
            if (hotel?._id) hotelsById[String(hotel._id)] = hotel;
          });
          const keys = new Set();
          (Array.isArray(rows) ? rows : []).forEach((acc) => {
            const guestIds = [
              ...(Array.isArray(acc.guestIds) ? acc.guestIds : []),
              acc.guestId,
              acc.mainGuestId,
            ]
              .filter(Boolean)
              .map(String);
            const attendeeIds = [
              ...(Array.isArray(acc.attendeeIds) ? acc.attendeeIds : []),
            ]
              .filter(Boolean)
              .map(String);

            if (acc.guestModel === "AttendeeMaster" && acc.guestId) {
              attendeeIds.push(String(acc.guestId));
            }

            guestIds.forEach((gid) => keys.add(`${gid}-null`));
            const primaryGuest = guestIds[0];
            attendeeIds.forEach((aid) => {
              if (primaryGuest) keys.add(`${primaryGuest}-${aid}`);
              keys.add(`attendee-${aid}`);
            });
          });
          setEligiblePersonKeys(keys);
          setAssignmentRecords({
            accommodations: Array.isArray(rows) ? rows : [],
            hotelsById,
            travels: [],
            seatDocs: [],
            loaded: true,
          });
          return;
        }

        if (inviteKind === "travel") {
          const res = await axios.get(getAllTravel(selectedEvent.eventId));
          const rows = res.data?.data || res.data || [];
          const keys = new Set();
          (Array.isArray(rows) ? rows : []).forEach((travel) => {
            const gid = travel.guestId ? String(travel.guestId) : "";
            const aid = travel.attendeeId ? String(travel.attendeeId) : null;
            if (!gid) return;
            if (aid) {
              keys.add(`${gid}-${aid}`);
              keys.add(`attendee-${aid}`);
            } else {
              keys.add(`${gid}-null`);
            }
          });
          setEligiblePersonKeys(keys);
          setAssignmentRecords({
            accommodations: [],
            hotelsById: {},
            travels: Array.isArray(rows) ? rows : [],
            seatDocs: [],
            loaded: true,
          });
          return;
        }

        if (inviteKind === "seat") {
          const res = await axios.get(getSeatAllocation(selectedEvent.eventId));
          const rows = res.data?.data || res.data || [];
          const keys = new Set();
          (Array.isArray(rows) ? rows : []).forEach((doc) => {
            const gid = doc.guestId ? String(doc.guestId) : "";
            const aid = doc.attendeeId ? String(doc.attendeeId) : null;
            const seats = Array.isArray(doc.allocatedSeat)
              ? doc.allocatedSeat
              : [];
            if (!gid || seats.length === 0) return;

            const matchesOccasion =
              formData.occasionId === "all"
                ? true
                : seats.some(
                    (s) => String(s.occasionId) === String(formData.occasionId)
                  );
            if (!matchesOccasion) return;

            if (aid) {
              keys.add(`${gid}-${aid}`);
              keys.add(`attendee-${aid}`);
            } else {
              keys.add(`${gid}-null`);
            }
          });
          setEligiblePersonKeys(keys);
          setAssignmentRecords({
            accommodations: [],
            hotelsById: {},
            travels: [],
            seatDocs: Array.isArray(rows) ? rows : [],
            loaded: true,
          });
          return;
        }

        if (inviteKind === "itinerary") {
          const [trackRes, accRes, hotelRes, travelRes, seatRes] =
            await Promise.all([
              axios.get(getTrackedInvites(selectedEvent.eventId)),
              axios.get(getGuestAccomodation(selectedEvent.eventId)),
              axios.get(getHotelsByEventId(selectedEvent.eventId)),
              axios.get(getAllTravel(selectedEvent.eventId)),
              axios.get(getSeatAllocation(selectedEvent.eventId)),
            ]);
          const rows = trackRes.data?.data || trackRes.data || [];
          const accRows = accRes.data?.data || accRes.data || [];
          const hotels = hotelRes.data?.data || hotelRes.data || [];
          const travelRows = travelRes.data?.data || travelRes.data || [];
          const seatRows = seatRes.data?.data || seatRes.data || [];
          const hotelsById = {};
          (Array.isArray(hotels) ? hotels : []).forEach((hotel) => {
            if (hotel?._id) hotelsById[String(hotel._id)] = hotel;
          });
          const keys = new Set();
          (Array.isArray(rows) ? rows : []).forEach((doc) => {
            const gid = doc.guestId ? String(doc.guestId) : "";
            if (!gid) return;
            const aid = doc.attendeeId ? String(doc.attendeeId) : null;
            const rsvps = Array.isArray(doc.rsvps) ? doc.rsvps : [];

            // Whole wedding: only explicit RSVP yes answers count
            const hasYes = rsvps.some((r) => r.response === "yes");

            if (!hasYes) return;

            if (aid) {
              keys.add(`${gid}-${aid}`);
              keys.add(`attendee-${aid}`);
            } else {
              keys.add(`${gid}-null`);
            }
          });
          setEligiblePersonKeys(keys);
          setAssignmentRecords({
            accommodations: Array.isArray(accRows) ? accRows : [],
            hotelsById,
            travels: Array.isArray(travelRows) ? travelRows : [],
            seatDocs: Array.isArray(seatRows) ? seatRows : [],
            loaded: true,
          });
        }
      } catch (error) {
        console.error("Failed to load invite eligibility:", error);
        setEligiblePersonKeys(new Set());
        setAssignmentRecords({
          accommodations: [],
          hotelsById: {},
          travels: [],
          seatDocs: [],
          loaded: true,
        });
        toast.error("Failed to load applicable guests for this invite type.");
      }
    };

    loadEligibility();
  }, [selectedEvent?.eventId, inviteKind, formData.occasionId]);

  useEffect(() => {
    if (!hasNoAssignments) return;
    toast.error(`${emptyAssignmentCopy.title}. ${emptyAssignmentCopy.body}`);
  }, [hasNoAssignments, emptyAssignmentCopy.title, emptyAssignmentCopy.body]);

  useEffect(() => {
    const loadAllocations = async () => {
      const { formatted, regNoMap } = await fetchAllocations(
        selectedEvent?.eventId
      );
      setSelectedAllocations(formatted);
      setRegNoMap(regNoMap);
    };

    loadAllocations();
  }, [selectedEvent]);

  // Fetch guests with attendees
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        if (!selectedEvent?.eventId) return;
        const data = await getMainGuestsAndAttendees(selectedEvent.eventId);
        setGuestWithAttendees(data);
        console.log("Guest & Attendee list fetched successfully:", data);
      } catch (error) {
        console.error("Error fetching guest & attendee list:", error);
        toast.error("Failed to fetch guest & attendee list.");
      }
    };

    fetchGuests();
  }, [selectedEvent]);

  const getFullName = (guest) => {
    if (!guest) return ""; // Prevent crash
    return [guest.prefix, guest.firstName, guest.middleName, guest.lastName]
      .filter(Boolean)
      .join(" ");
  };

  const isPersonEligible = (guestId, attendeeId = null) => {
    if (!eligiblePersonKeys) return true;
    const gid = String(guestId);
    let matched = false;
    if (attendeeId) {
      const aid = String(attendeeId);
      matched =
        eligiblePersonKeys.has(`${gid}-${aid}`) ||
        eligiblePersonKeys.has(`attendee-${aid}`);
    } else {
      matched = eligiblePersonKeys.has(`${gid}-null`);
    }
    if (!matched) return false;

    // Itinerary links require a registration number
    if (inviteKind === "itinerary") {
      const regKey = attendeeId ? `${gid}-${attendeeId}` : `${gid}-null`;
      return Boolean(regNoMap[regKey]);
    }
    return true;
  };

  const getContactGuestId = (contact) =>
    String(contact?.guestId || (contact?.type === "Guest" ? contact.id : ""));

  const getContactAttendeeId = (contact) =>
    contact?.type === "Attendee" ? String(contact.id) : null;

  const getAssignmentFieldValues = useCallback(
    (contact, occasionId = null) => {
      const values = {};
      if (!contact || inviteKind === "invitation" || inviteKind === "itinerary")
        return values;

      const guestId = getContactGuestId(contact);
      const attendeeId = getContactAttendeeId(contact);

      if (inviteKind === "accommodation") {
        const acc = (assignmentRecords.accommodations || []).find((row) => {
          const attendeeIds = [
            ...(Array.isArray(row.attendeeIds) ? row.attendeeIds : []),
            row.guestModel === "AttendeeMaster" ? row.guestId : null,
          ]
            .filter(Boolean)
            .map(String);
          if (attendeeId) return attendeeIds.includes(attendeeId);

          const guestIds = [
            ...(Array.isArray(row.guestIds) ? row.guestIds : []),
            row.guestId,
            row.mainGuestId,
          ]
            .filter(Boolean)
            .map(String);
          return guestIds.includes(guestId);
        });
        if (!acc) return values;
        const hotel = assignmentRecords.hotelsById[String(acc.hotelId)] || {};
        const roomIdSet = new Set((acc.roomIds || []).map(String));
        const roomNumbers = (hotel.rooms || [])
          .filter((room) => roomIdSet.has(String(room._id)))
          .map((room) => room.roomNumber)
          .filter(Boolean);
        const hotelAddress = formatAddressValue(
          hotel.address || hotel.hotelAddress || ""
        );
        const roomNumbersLabel = roomNumbers.join(", ");
        const roomLabel =
          acc.roomType && roomNumbersLabel
            ? `${acc.roomType}, Room ${roomNumbersLabel}`
            : acc.roomType ||
              (roomNumbersLabel ? `Room ${roomNumbersLabel}` : "");
        Object.assign(values, {
          hotelName: hotel.hotelName || hotel.name || "",
          hotelAddress,
          roomType: acc.roomType || "",
          roomNumbers: roomNumbersLabel,
          roomLabel,
          checkInDate: acc.checkInDate || "",
          checkInTime: acc.checkInTime || "",
          checkOutDate: acc.checkOutDate || "",
          checkOutTime: acc.checkOutTime || "",
          noOfRooms: acc.noOfRooms ?? "",
          occupancy: acc.occupancy ?? "",
          stayStatus: acc.stayStatus || "",
          floorPreference: acc.floorPreference || "",
          roomFacing: acc.roomFacing || "",
          roomPreference: acc.roomPreference || "",
          specialRequirements: acc.specialRequirements || "",
        });
        return values;
      }

      if (inviteKind === "travel") {
        const travel = (assignmentRecords.travels || []).find((row) => {
          const gid = String(row.guestId || "");
          const aid = row.attendeeId ? String(row.attendeeId) : null;
          if (attendeeId) return gid === guestId && aid === attendeeId;
          return gid === guestId && !aid;
        });
        if (!travel) return values;
        Object.assign(values, {
          arrivalDate: travel.arrivalDate || "",
          arrivalTime: travel.arrivalTime || "",
          arrivalMode: travel.arrivalMode || travel.arrivalModeOfTravel || "",
          arrivalFlightTrainBusNo: travel.arrivalFlightTrainBusNo || "",
          arrivalPNRNo: travel.arrivalPNRNo || "",
          arrivalPickUpLocation: travel.arrivalPickUpLocation || "",
          pickupRequired: travel.pickupRequired ? "Yes" : "",
          pickupLocation:
            travel.pickupLocation || travel.arrivalPickUpLocation || "",
          pickupDate: travel.pickupDate || "",
          pickupTime: travel.pickupTime || "",
          pickupVehicle: [travel.pickupVehicle, travel.pickupVehicleNumber]
            .filter(Boolean)
            .join(" / "),
          pickupChauffeur: travel.pickupChauffeur || "",
          pickupDriverMobile: travel.pickupDriverMobile || "",
          pickupInstructions: travel.pickupInstructions || "",
          departureDate: travel.departureDate || "",
          departureTime: travel.departureTime || "",
          departureMode:
            travel.departureMode || travel.departureModeOfTravel || "",
          departureFlightTrainBusNo: travel.departureFlightTrainBusNo || "",
          departurePNRNo: travel.departurePNRNo || "",
          departureDropLocation: travel.departureDropLocation || "",
          dropRequired: travel.dropRequired ? "Yes" : "",
          dropLocation: travel.dropLocation || travel.departureDropLocation || "",
          dropDate: travel.dropDate || "",
          dropTime: travel.dropTime || "",
          dropVehicle: [travel.dropVehicle, travel.dropVehicleNumber]
            .filter(Boolean)
            .join(" / "),
          dropChauffeur: travel.dropChauffeur || "",
          dropDriverMobile: travel.dropDriverMobile || "",
          dropInstructions: travel.dropInstructions || "",
          localVehicle: travel.localVehicleType || travel.carType || "",
          itineraryNotes: travel.itineraryNotes || "",
          _pickupApplicable: Boolean(
            travel.pickupRequired ||
              travel.pickupDate ||
              travel.pickupTime ||
              travel.pickupVehicle ||
              travel.pickupVehicleNumber ||
              travel.pickupChauffeur ||
              travel.pickupDriverMobile ||
              travel.pickupInstructions ||
              (travel.pickupLocation &&
                travel.pickupLocation !== travel.arrivalPickUpLocation)
          ),
          _dropApplicable: Boolean(
            travel.dropRequired ||
              travel.dropDate ||
              travel.dropTime ||
              travel.dropVehicle ||
              travel.dropVehicleNumber ||
              travel.dropChauffeur ||
              travel.dropDriverMobile ||
              travel.dropInstructions ||
              (travel.dropLocation &&
                travel.dropLocation !== travel.departureDropLocation)
          ),
        });
        return values;
      }

      if (inviteKind === "seat") {
        const doc = (assignmentRecords.seatDocs || []).find((row) => {
          const gid = String(row.guestId || "");
          const aid = row.attendeeId ? String(row.attendeeId) : null;
          if (attendeeId) return gid === guestId && aid === attendeeId;
          return gid === guestId && !aid;
        });
        const seats = Array.isArray(doc?.allocatedSeat)
          ? doc.allocatedSeat
          : [];
        const filtered = occasionId
          ? seats.filter((s) => String(s.occasionId) === String(occasionId))
          : formData.occasionId === "all"
            ? seats
            : seats.filter(
                (s) => String(s.occasionId) === String(formData.occasionId)
              );
        if (!filtered.length) return values;
        const primary = filtered[0];
        Object.assign(values, {
          seatLabel: filtered
            .map((seat) => {
              const occasionName =
                occasions.find((o) => String(o._id) === String(seat.occasionId))
                  ?.OccasionName || "Occasion";
              return `${occasionName}: ${formatSeatLabel(seat) || "Assigned"}`;
            })
            .join("; "),
          tableNumber: primary?.tableAllocation?.tableNumber ?? "",
          rowNumber: primary?.rowAllocation?.rowNumber ?? "",
          seatNumber:
            primary?.tableAllocation?.seatNumber ??
            primary?.rowAllocation?.seatNumber ??
            "",
        });
      }

      return values;
    },
    [inviteKind, assignmentRecords, formData.occasionId, occasions]
  );

  const getAssignmentCardsForContact = useCallback(
    (contact, occasionId = null) => {
      if (!contact || inviteKind === "invitation") return [];

      const guestId = getContactGuestId(contact);
      const attendeeId = getContactAttendeeId(contact);
      const values = getAssignmentFieldValues(contact, occasionId);

      if (inviteKind === "accommodation") {
        if (!values.hotelName && !values.checkInDate && !values.roomType) {
          return [];
        }
        const stayRows = [
          { label: "Hotel", value: values.hotelName },
          { label: "Address", value: values.hotelAddress },
          { label: "Room type", value: values.roomType },
          {
            label: "Check-in",
            value: [values.checkInDate, values.checkInTime]
              .filter(Boolean)
              .join(" · "),
          },
          {
            label: "Check-out",
            value: [values.checkOutDate, values.checkOutTime]
              .filter(Boolean)
              .join(" · "),
          },
          { label: "Rooms", value: values.noOfRooms },
          { label: "Occupancy", value: values.occupancy },
          { label: "Status", value: values.stayStatus },
        ].filter((row) => row.value !== undefined && row.value !== null && String(row.value).trim() !== "");

        const preferenceRows = [
          { label: "Floor", value: values.floorPreference },
          { label: "Facing", value: values.roomFacing },
          { label: "Preference", value: values.roomPreference },
          { label: "Special needs", value: values.specialRequirements },
        ].filter((row) => row.value !== undefined && row.value !== null && String(row.value).trim() !== "");

        const cards = [
          {
            title: "Stay details",
            subtitle: "Your accommodation for the event",
            rows: stayRows,
          },
        ];
        if (preferenceRows.length) {
          cards.push({
            title: "Preferences",
            subtitle: "Noted for your comfort",
            rows: preferenceRows,
          });
        }
        return cards;
      }

      if (inviteKind === "travel") {
        const arrivalRows = [
          {
            label: "Date / Time",
            value: [values.arrivalDate, values.arrivalTime]
              .filter(Boolean)
              .join(" · "),
          },
          { label: "Mode", value: values.arrivalMode },
          { label: "Flight / Train / Bus", value: values.arrivalFlightTrainBusNo },
          { label: "PNR", value: values.arrivalPNRNo },
          !values._pickupApplicable
            ? { label: "Pickup point", value: values.arrivalPickUpLocation }
            : null,
        ]
          .filter(Boolean)
          .filter((row) => String(row.value || "").trim());

        const pickupRows = values._pickupApplicable
          ? [
              { label: "Location", value: values.pickupLocation },
              {
                label: "Date / Time",
                value: [values.pickupDate, values.pickupTime]
                  .filter(Boolean)
                  .join(" · "),
              },
              { label: "Vehicle", value: values.pickupVehicle },
              { label: "Chauffeur", value: values.pickupChauffeur },
              { label: "Driver mobile", value: values.pickupDriverMobile },
              { label: "Instructions", value: values.pickupInstructions },
            ].filter((row) => String(row.value || "").trim())
          : [];

        const departureRows = [
          {
            label: "Date / Time",
            value: [values.departureDate, values.departureTime]
              .filter(Boolean)
              .join(" · "),
          },
          { label: "Mode", value: values.departureMode },
          {
            label: "Flight / Train / Bus",
            value: values.departureFlightTrainBusNo,
          },
          { label: "PNR", value: values.departurePNRNo },
          !values._dropApplicable
            ? { label: "Drop point", value: values.departureDropLocation }
            : null,
        ]
          .filter(Boolean)
          .filter((row) => String(row.value || "").trim());

        const dropRows = values._dropApplicable
          ? [
              { label: "Location", value: values.dropLocation },
              {
                label: "Date / Time",
                value: [values.dropDate, values.dropTime]
                  .filter(Boolean)
                  .join(" · "),
              },
              { label: "Vehicle", value: values.dropVehicle },
              { label: "Chauffeur", value: values.dropChauffeur },
              { label: "Driver mobile", value: values.dropDriverMobile },
              { label: "Instructions", value: values.dropInstructions },
            ].filter((row) => String(row.value || "").trim())
          : [];

        const localRows = [
          { label: "Local vehicle", value: values.localVehicle },
          { label: "Notes", value: values.itineraryNotes },
        ].filter((row) => String(row.value || "").trim());

        const cards = [];
        if (arrivalRows.length) {
          cards.push({
            title: "Arrival",
            subtitle: "Getting you to the venue",
            rows: arrivalRows,
          });
        }
        if (pickupRows.length) {
          cards.push({
            title: "Pickup",
            subtitle: "Pickup assistance arranged for you",
            rows: pickupRows,
          });
        }
        if (departureRows.length) {
          cards.push({
            title: "Departure",
            subtitle: "Your return journey",
            rows: departureRows,
          });
        }
        if (dropRows.length) {
          cards.push({
            title: "Drop",
            subtitle: "Drop assistance arranged for you",
            rows: dropRows,
          });
        }
        if (localRows.length) {
          cards.push({
            title: "Local transport",
            subtitle: "During the event",
            rows: localRows,
          });
        }
        return cards;
      }

      if (inviteKind === "seat") {
        const doc = (assignmentRecords.seatDocs || []).find((row) => {
          const gid = String(row.guestId || "");
          const aid = row.attendeeId ? String(row.attendeeId) : null;
          if (attendeeId) return gid === guestId && aid === attendeeId;
          return gid === guestId && !aid;
        });
        const seats = Array.isArray(doc?.allocatedSeat)
          ? doc.allocatedSeat
          : [];
        const filtered = occasionId
          ? seats.filter((s) => String(s.occasionId) === String(occasionId))
          : formData.occasionId === "all"
            ? seats
            : seats.filter(
                (s) => String(s.occasionId) === String(formData.occasionId)
              );

        return filtered.map((seat) => {
          const occasionIndex = Math.max(
            0,
            occasions.findIndex(
              (o) => String(o._id) === String(seat.occasionId)
            )
          );
          const occasion = occasions[occasionIndex];
          const theme = getOccasionThemeForId(occasions, seat.occasionId);
          const rows = [
            {
              label: "Table",
              value: seat.tableAllocation?.tableNumber ?? "",
            },
            {
              label: "Row",
              value: seat.rowAllocation?.rowNumber ?? "",
            },
            {
              label: "Seat",
              value:
                seat.tableAllocation?.seatNumber ??
                seat.rowAllocation?.seatNumber ??
                "",
            },
            { label: "Type", value: seat.allocationType || "" },
            { label: "Remarks", value: seat.remarks || "" },
          ].filter((row) => String(row.value || "").trim() !== "");

          return {
            title: occasion?.OccasionName || "Occasion",
            subtitle: "Your seat allotment",
            rows: rows.length
              ? rows
              : [{ label: "Allocation", value: formatSeatLabel(seat) || "Assigned" }],
            theme,
            occasionIndex,
          };
        });
      }

      return [];
    },
    [
      inviteKind,
      getAssignmentFieldValues,
      assignmentRecords.seatDocs,
      formData.occasionId,
      occasions,
    ]
  );

  const getDetailsTextForContact = useCallback(
    (contact, occasionId = null) => {
      const values = getAssignmentFieldValues(contact, occasionId);
      if (!Object.keys(values).length) {
        if (inviteKind === "accommodation") {
          return "No accommodation details found for this guest.";
        }
        if (inviteKind === "travel") {
          return "No travel details found for this guest.";
        }
        if (inviteKind === "seat") {
          return "No seat allocation found for this guest.";
        }
        return "";
      }

      if (inviteKind === "accommodation") {
        return [
          detailLine("Hotel", values.hotelName),
          detailLine("Address", values.hotelAddress),
          detailLine("Room type", values.roomType),
          detailLine(
            "Check-in",
            [values.checkInDate, values.checkInTime].filter(Boolean).join(" ")
          ),
          detailLine(
            "Check-out",
            [values.checkOutDate, values.checkOutTime].filter(Boolean).join(" ")
          ),
          detailLine("No. of rooms", values.noOfRooms),
          detailLine("Occupancy", values.occupancy),
          detailLine("Stay status", values.stayStatus),
          detailLine("Floor preference", values.floorPreference),
          detailLine("Room facing", values.roomFacing),
          detailLine("Room preference", values.roomPreference),
          detailLine("Special requirements", values.specialRequirements),
        ]
          .filter(Boolean)
          .join("\n");
      }

      if (inviteKind === "travel") {
        return [
          "Arrival",
          detailLine(
            "  Date / Time",
            [values.arrivalDate, values.arrivalTime].filter(Boolean).join(" ")
          ),
          detailLine("  Mode", values.arrivalMode),
          detailLine("  Flight/Train/Bus", values.arrivalFlightTrainBusNo),
          detailLine("  PNR", values.arrivalPNRNo),
          values._pickupApplicable
            ? null
            : detailLine("  Pickup point", values.arrivalPickUpLocation),
          values._pickupApplicable ? "Pickup" : null,
          values._pickupApplicable
            ? detailLine("  Location", values.pickupLocation)
            : null,
          values._pickupApplicable
            ? detailLine(
                "  Date / Time",
                [values.pickupDate, values.pickupTime].filter(Boolean).join(" ")
              )
            : null,
          values._pickupApplicable
            ? detailLine("  Vehicle", values.pickupVehicle)
            : null,
          values._pickupApplicable
            ? detailLine("  Chauffeur", values.pickupChauffeur)
            : null,
          values._pickupApplicable
            ? detailLine("  Driver mobile", values.pickupDriverMobile)
            : null,
          values._pickupApplicable
            ? detailLine("  Instructions", values.pickupInstructions)
            : null,
          "Departure",
          detailLine(
            "  Date / Time",
            [values.departureDate, values.departureTime]
              .filter(Boolean)
              .join(" ")
          ),
          detailLine("  Mode", values.departureMode),
          detailLine("  Flight/Train/Bus", values.departureFlightTrainBusNo),
          detailLine("  PNR", values.departurePNRNo),
          values._dropApplicable
            ? null
            : detailLine("  Drop point", values.departureDropLocation),
          values._dropApplicable ? "Drop" : null,
          values._dropApplicable
            ? detailLine("  Location", values.dropLocation)
            : null,
          values._dropApplicable
            ? detailLine(
                "  Date / Time",
                [values.dropDate, values.dropTime].filter(Boolean).join(" ")
              )
            : null,
          values._dropApplicable
            ? detailLine("  Vehicle", values.dropVehicle)
            : null,
          values._dropApplicable
            ? detailLine("  Chauffeur", values.dropChauffeur)
            : null,
          values._dropApplicable
            ? detailLine("  Driver mobile", values.dropDriverMobile)
            : null,
          values._dropApplicable
            ? detailLine("  Instructions", values.dropInstructions)
            : null,
          detailLine("Local vehicle", values.localVehicle),
          detailLine("Itinerary notes", values.itineraryNotes),
        ]
          .filter(Boolean)
          .join("\n");
      }

      if (inviteKind === "seat") {
        return detailLine("Seat", values.seatLabel) || "";
      }

      return "";
    },
    [getAssignmentFieldValues, inviteKind]
  );

  const formatInviteDate = (value) => {
    if (!value) return "";
    const parsed = new Date(String(value).split("T")[0]);
    return Number.isNaN(parsed.getTime())
      ? String(value).split("T")[0]
      : parsed.toLocaleDateString();
  };

  const getItineraryHighlightsForContact = useCallback(
    (contact) => {
      if (!contact) {
        return { occasions: [], stay: [], travel: [], seats: [] };
      }

      const guestId = getContactGuestId(contact);
      const attendeeId = getContactAttendeeId(contact);
      const allocKey = attendeeId
        ? `${guestId}-${attendeeId}`
        : `${guestId}-null`;
      const allocatedIds = (selectedAllocations[allocKey] || []).map(String);

      const occasionLines = occasions
        .filter((occ) => allocatedIds.includes(String(occ._id)))
        .slice(0, 3)
        .map((occ) => {
          const when = [formatInviteDate(occ.Date), occ.time]
            .filter(Boolean)
            .join(" · ");
          return when
            ? `${occ.OccasionName} (${when})`
            : occ.OccasionName || "Occasion";
        });

      const acc = (assignmentRecords.accommodations || []).find((row) => {
        const attendeeIds = [
          ...(Array.isArray(row.attendeeIds) ? row.attendeeIds : []),
          row.guestModel === "AttendeeMaster" ? row.guestId : null,
        ]
          .filter(Boolean)
          .map(String);
        if (attendeeId) return attendeeIds.includes(attendeeId);
        const guestIds = [
          ...(Array.isArray(row.guestIds) ? row.guestIds : []),
          row.guestId,
          row.mainGuestId,
        ]
          .filter(Boolean)
          .map(String);
        return guestIds.includes(String(guestId));
      });
      const hotel = acc?.hotelId
        ? assignmentRecords.hotelsById[String(acc.hotelId)]
        : null;
      const stayLines = [];
      if (hotel?.hotelName || acc) {
        stayLines.push(hotel?.hotelName || "Hotel assigned");
        const dates = [
          formatInviteDate(acc?.checkInDate),
          formatInviteDate(acc?.checkOutDate),
        ]
          .filter(Boolean)
          .join(" → ");
        if (dates) stayLines.push(dates);
        if (acc?.roomType) stayLines.push(acc.roomType);
      }

      const travel = (assignmentRecords.travels || []).find((row) => {
        const gid = row.guestId ? String(row.guestId) : "";
        const aid = row.attendeeId ? String(row.attendeeId) : null;
        if (String(gid) !== String(guestId)) return false;
        if (attendeeId) return String(aid) === String(attendeeId);
        return !aid;
      });
      const travelLines = [];
      if (travel) {
        const arrival = [
          formatInviteDate(travel.arrivalDate),
          travel.arrivalTime,
          travel.arrivalMode || travel.arrivalModeOfTravel,
        ]
          .filter(Boolean)
          .join(" · ");
        if (arrival) travelLines.push(`Arrival: ${arrival}`);
        const departure = [
          formatInviteDate(travel.departureDate),
          travel.departureTime,
          travel.departureMode || travel.departureModeOfTravel,
        ]
          .filter(Boolean)
          .join(" · ");
        if (departure) travelLines.push(`Departure: ${departure}`);
        if (travel.pickupRequired) {
          travelLines.push(
            `Pickup: ${travel.pickupLocation || travel.arrivalPickUpLocation || "Arranged"}`
          );
        }
      }

      const seatDoc = (assignmentRecords.seatDocs || []).find((row) => {
        const gid = row.guestId ? String(row.guestId) : "";
        const aid = row.attendeeId ? String(row.attendeeId) : null;
        if (String(gid) !== String(guestId)) return false;
        if (attendeeId) return String(aid) === String(attendeeId);
        return !aid;
      });
      const seatLines = (seatDoc?.allocatedSeat || [])
        .slice(0, 3)
        .map((seat) => {
          const occName =
            occasions.find((o) => String(o._id) === String(seat.occasionId))
              ?.OccasionName || "Occasion";
          const label = formatSeatLabel(seat) || "Assigned";
          return `${occName}: ${label}`;
        });

      return {
        guestFirstName: contact?.firstName || "",
        occasions: occasionLines,
        stay: stayLines,
        travel: travelLines,
        seats: seatLines,
      };
    },
    [assignmentRecords, occasions, selectedAllocations]
  );

  const getPlaceholderValuesForContact = useCallback(
    (contact, { occasionName = "", occasionId = null, detailsText = "" } = {}) => {
      const assignmentValues = getAssignmentFieldValues(contact, occasionId);
      const itineraryUrl = buildGuestItineraryUrl(
        selectedEvent?.eventId,
        contact?.regNo
      );
      const displayGuestName =
        inviteKind === "itinerary"
          ? contact?.firstName || contact?.name || ""
          : contact?.name || "";
      return {
        guestName: displayGuestName,
        prefix: contact?.prefix || "",
        firstName: contact?.firstName || "",
        middleName: contact?.middleName || "",
        lastName: contact?.lastName || "",
        email: contact?.email || "",
        mobile: contact?.mobile || "",
        regNo: contact?.regNo || "",
        occasionName: occasionName || "",
        eventName: selectedEvent?.eventName || "",
        itineraryUrl,
        details: detailsText || getDetailsTextForContact(contact, occasionId),
        ...assignmentValues,
      };
    },
    [
      getAssignmentFieldValues,
      getDetailsTextForContact,
      selectedEvent?.eventName,
      selectedEvent?.eventId,
      inviteKind,
    ]
  );

  const filteredGuestWithAttendees = guestWithAttendees
    .map((entry) => {
      const mainGuest = entry.guest;
      const attendees = entry.attendees || [];

      const mainKey = `${mainGuest._id}-null`;
      const allocatedOccasionsMain = selectedAllocations[mainKey] || [];

      // 🔹 Show guest if the selected occasion is allocated
      const isMainAllocated =
        formData.occasionId === "all"
          ? allocatedOccasionsMain.length > 0
          : !formData.occasionId ||
            allocatedOccasionsMain.includes(formData.occasionId);

      const isMainIncluded =
        isMainAllocated && isPersonEligible(mainGuest._id, null);

      const filteredAttendees = attendees.filter((attendee) => {
        const key = `${mainGuest._id}-${attendee._id}`;
        const allocatedOccasions = selectedAllocations[key] || [];
        const allocated =
          formData.occasionId === "all"
            ? allocatedOccasions.length > 0
            : !formData.occasionId ||
              allocatedOccasions.includes(formData.occasionId);
        return allocated && isPersonEligible(mainGuest._id, attendee._id);
      });

      if (!isMainIncluded && filteredAttendees.length === 0) return null;

      // 🔹 Guest Invite
      const guestInvite = formData.invitationType
        ? savedInvites.find(
            (invite) =>
              invite.guestId === mainGuest._id &&
              !invite.attendeeId &&
              invite.invitations?.some(
                (inv) =>
                  inv.type === formData.invitationType &&
                  String(inv.inviteKind || "invitation") ===
                    String(inviteKind || "invitation") &&
                  (formData.occasionId === "all" ||
                    !formData.occasionId ||
                    String(inv.occasionId) === String(formData.occasionId))
              )
          )
        : null;

      const guestCurrentInvitation = guestInvite?.invitations.find(
        (inv) =>
          inv.type === formData.invitationType &&
          String(inv.inviteKind || "invitation") ===
            String(inviteKind || "invitation") &&
          (formData.occasionId === "all" ||
            !formData.occasionId ||
            String(inv.occasionId) === String(formData.occasionId))
      );

      // 🔹 Attendees with invites
      const attendeesWithInvites = filteredAttendees.map((attendee) => {
        const attendeeInvite = formData.invitationType
          ? savedInvites.find(
              (invite) =>
                invite.guestId === mainGuest._id &&
                invite.attendeeId === attendee._id &&
                invite.invitations?.some(
                  (inv) =>
                    inv.type === formData.invitationType &&
                    String(inv.inviteKind || "invitation") ===
                      String(inviteKind || "invitation") &&
                    (formData.occasionId === "all" ||
                      !formData.occasionId ||
                      String(inv.occasionId) === String(formData.occasionId))
                )
            )
          : null;

        const attendeeCurrentInvitation = attendeeInvite?.invitations.find(
          (inv) =>
            inv.type === formData.invitationType &&
            String(inv.inviteKind || "invitation") ===
              String(inviteKind || "invitation") &&
            (formData.occasionId === "all" ||
              !formData.occasionId ||
              String(inv.occasionId) === String(formData.occasionId))
        );

        return {
          ...attendee,
          currentInvitation: attendeeCurrentInvitation || null,
          inviteDocId: attendeeInvite?._id || null,
          email: attendee.email || null, // 🔹 added
          mobileNo: attendee.mobileNo || null, // 🔹 added
        };
      });

      return {
        guest: isMainIncluded
          ? {
              ...mainGuest,
              currentInvitation: guestCurrentInvitation || null,
              inviteDocId: guestInvite?._id || null,
              email: mainGuest.email || null, // 🔹 added
              mobileNo: mainGuest.mobileNo || null, // 🔹 added
            }
          : null,
        attendees: attendeesWithInvites,
      };
    })
    .filter(Boolean)
    .filter((entry) => {
      const mainName = getFullName(entry.guest || {}).toLowerCase();
      const matchesMain =
        entry.guest && mainName.includes(searchTerm.toLowerCase());

      const attendeeMatches = (entry.attendees || []).some((attendee) =>
        getFullName(attendee).toLowerCase().includes(searchTerm.toLowerCase())
      );

      return matchesMain || attendeeMatches;
    });

  const selectedContacts = filteredGuestWithAttendees.flatMap((entry) => {
    const selected = [];

    // main guest
    if (
      entry.guest &&
      entry.guest._id &&
      selectedRows[`guest-${entry.guest._id}`]
    ) {
      const key = `${entry.guest._id}-null`;
      const regNo = regNoMap[key] || null;

      selected.push({
        type: "Guest",
        id: entry.guest._id,
        guestId: entry.guest._id,
        prefix: entry.guest.prefix || "",
        firstName: entry.guest.firstName || "",
        middleName: entry.guest.middleName || "",
        lastName: entry.guest.lastName || "",
        name: getFullName(entry.guest),
        email: entry.guest.email,
        mobile: entry.guest.mobileNo,
        regNo,
      });
    }

    // attendees
    entry.attendees?.forEach((attendee) => {
      if (
        attendee?._id &&
        entry.guest?._id &&
        selectedRows[`attendee-${attendee._id}`]
      ) {
        const key = `${entry.guest._id}-${attendee._id}`;
        const regNo = regNoMap[key] || null;

        selected.push({
          type: "Attendee",
          id: attendee._id,
          guestId: entry.guest._id,
          prefix: attendee.prefix || "",
          firstName: attendee.firstName || "",
          middleName: attendee.middleName || "",
          lastName: attendee.lastName || "",
          name: getFullName(attendee),
          email: attendee.email,
          mobile: attendee.mobileNo,
          regNo,
        });
      }
    });

    return selected;
  });

  console.log("Selected Contacts with RegNo (extended):", selectedContacts);

  const getOccasionNames = useCallback(() => {
    if (formData.occasionId === "all") {
      return occasions.map((o) => o.OccasionName).filter(Boolean).join(", ");
    }
    const selectedOccasion = occasions.find((o) => o._id === formData.occasionId);
    return selectedOccasion ? selectedOccasion.OccasionName : "";
  }, [formData.occasionId, occasions]);

  const getOccasionNamesForIds = useCallback(
    (occasionIds) =>
      (occasionIds || [])
        .map(
          (id) =>
            occasions.find((o) => String(o._id) === String(id))?.OccasionName
        )
        .filter(Boolean)
        .join(", "),
    [occasions]
  );

  const getRsvpOccasionIds = useCallback(() => {
    if (formData.occasionId === "all") {
      return occasions.map((o) => o._id).filter(Boolean);
    }
    return formData.occasionId ? [formData.occasionId] : [];
  }, [formData.occasionId, occasions]);

  const getMediaItemsForOccasionIds = useCallback(
    (occasionIds) => {
      const idSet = new Set((occasionIds || []).map(String));
      return (occasions || [])
        .filter((o) => o.MediaUpload)
        .filter((o) =>
          idSet.size === 0 ? true : idSet.has(String(o._id))
        )
        .map((occasion) => {
          const mediaPath = String(occasion.MediaUpload || "")
            .replace(/\\/g, "/")
            .replace(/^\//, "");
          const ext = mediaPath.split(".").pop().toLowerCase();
          return {
            id: `occasion-${occasion._id}`,
            name: occasion.OccasionName || "Occasion media",
            filename: mediaPath.split("/").pop() || "media",
            url: `${FRONTEND_URL}/${mediaPath}`,
            isVideo: ["mp4", "mov", "avi", "mkv", "webm"].includes(ext),
            source: "occasion",
          };
        });
    },
    [occasions]
  );

  const clearEmailDeviceFiles = useCallback(() => {
    setEmailDeviceFiles((prev) => {
      prev.forEach((file) => {
        if (file.previewUrl && String(file.previewUrl).startsWith("blob:")) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      return [];
    });
  }, []);

  const handleEmailDeviceFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const nextFiles = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = String(reader.result || "");
              const base64 = result.includes(",")
                ? result.split(",")[1]
                : result;
              const isVideo = String(file.type || "").startsWith("video/");
              const isImage = String(file.type || "").startsWith("image/");
              resolve({
                id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
                  .toString(36)
                  .slice(2, 8)}`,
                name: file.name,
                filename: file.name,
                content: base64,
                mimeType: file.type || "application/octet-stream",
                previewUrl: isImage || isVideo ? URL.createObjectURL(file) : "",
                dataUrl: isImage ? result : "",
                isVideo,
                isImage,
                source: "device",
                size: file.size,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    );

    setEmailDeviceFiles((prev) => [...prev, ...nextFiles]);
    if (emailFileInputRef.current) emailFileInputRef.current.value = "";
  };

  const removeEmailDeviceFile = (fileId) => {
    setEmailDeviceFiles((prev) => {
      const target = prev.find((file) => file.id === fileId);
      if (target?.previewUrl && String(target.previewUrl).startsWith("blob:")) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((file) => file.id !== fileId);
    });
  };

  const getSelectedMediaItems = useCallback(() => {
    if (formData.occasionId === "all") {
      return getMediaItemsForOccasionIds(occasions.map((o) => o._id));
    }
    return getMediaItemsForOccasionIds([formData.occasionId]);
  }, [formData.occasionId, occasions, getMediaItemsForOccasionIds]);

  const closeEmailModal = useCallback(() => {
    clearEmailDeviceFiles();
    setEmailInviteCardUrl("");
    setEmailModal({
      isOpen: false,
      subject: "",
      content: "",
      footer: "Warm regards,\nTecogis Team",
      attachMedia: false,
      addQR: false,
    });
  }, [clearEmailDeviceFiles]);

  const emailPreviewOccasionMedia = useMemo(() => {
    if (splitInviteByOccasion && previewMailOccasionId) {
      return getMediaItemsForOccasionIds([previewMailOccasionId]);
    }
    return getSelectedMediaItems();
  }, [
    splitInviteByOccasion,
    previewMailOccasionId,
    getMediaItemsForOccasionIds,
    getSelectedMediaItems,
  ]);

  const emailActiveTheme = useMemo(() => {
    const occasionId =
      (splitInviteByOccasion && previewMailOccasionId) ||
      (formData.occasionId && formData.occasionId !== "all"
        ? formData.occasionId
        : null);
    return getOccasionThemeForId(occasions, occasionId);
  }, [
    splitInviteByOccasion,
    previewMailOccasionId,
    formData.occasionId,
    occasions,
  ]);

  const whatsappActiveTheme = useMemo(() => {
    const occasionId =
      (splitInviteByOccasion && previewWhatsAppOccasionId) ||
      (formData.occasionId && formData.occasionId !== "all"
        ? formData.occasionId
        : null);
    return getOccasionThemeForId(occasions, occasionId);
  }, [
    splitInviteByOccasion,
    previewWhatsAppOccasionId,
    formData.occasionId,
    occasions,
  ]);

  const previewRecipient = useMemo(() => {
    const isWhatsApp = isWhatsAppInvitationType(formData.invitationType);
    if (isWhatsApp) {
      return (
        selectedContacts.find((c) => c.mobile && c.regNo) ||
        selectedContacts.find((c) => c.mobile) ||
        selectedContacts.find((c) => c.regNo) ||
        selectedContacts[0] || {
          name: "Guest",
          email: "",
          mobile: "",
          regNo: "PREVIEW",
        }
      );
    }
    return (
      selectedContacts.find((c) => c.email && c.regNo) ||
      selectedContacts[0] || {
        name: "Guest",
        email: "",
        regNo: "",
      }
    );
  }, [selectedContacts, formData.invitationType]);

  const availableDynamicFields = useMemo(() => {
    const contacts =
      selectedContacts.length > 0
        ? selectedContacts
        : filteredGuestWithAttendees.flatMap((entry) => {
            const rows = [];
            if (entry.guest) {
              rows.push({
                type: "Guest",
                id: entry.guest._id,
                guestId: entry.guest._id,
                prefix: entry.guest.prefix || "",
                firstName: entry.guest.firstName || "",
                middleName: entry.guest.middleName || "",
                lastName: entry.guest.lastName || "",
                name: getFullName(entry.guest),
                email: entry.guest.email || "",
                mobile: entry.guest.mobileNo || "",
                regNo: regNoMap[`${entry.guest._id}-null`] || "",
              });
            }
            (entry.attendees || []).forEach((attendee) => {
              rows.push({
                type: "Attendee",
                id: attendee._id,
                guestId: entry.guest?._id,
                prefix: attendee.prefix || "",
                firstName: attendee.firstName || "",
                middleName: attendee.middleName || "",
                lastName: attendee.lastName || "",
                name: getFullName(attendee),
                email: attendee.email || "",
                mobile: attendee.mobileNo || "",
                regNo:
                  regNoMap[`${entry.guest?._id}-${attendee._id}`] || "",
              });
            });
            return rows;
          });

    const presentKeys = new Set();

    contacts.forEach((contact) => {
      const values = getPlaceholderValuesForContact(contact, {
        occasionName:
          formData.occasionId !== "all"
            ? occasions.find((o) => String(o._id) === String(formData.occasionId))
                ?.OccasionName || ""
            : "Occasion",
        occasionId:
          formData.occasionId !== "all" ? formData.occasionId : null,
      });
      Object.entries(values).forEach(([key, value]) => {
        if (String(key).startsWith("_")) return;
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          presentKeys.add(key);
        }
      });
    });

    // Context fields that are valid for this invite flow even if preview guest
    // doesn't currently have every value filled.
    if (selectedEvent?.eventName) presentKeys.add("eventName");
    if (
      inviteKind === "invitation" ||
      inviteKind === "seat" ||
      splitInviteByOccasion ||
      (formData.occasionId && formData.occasionId !== "all")
    ) {
      if (occasions.length > 0) presentKeys.add("occasionName");
    }
    if (inviteKind === "itinerary") {
      presentKeys.add("itineraryUrl");
      presentKeys.delete("details");
    } else if (inviteKind && inviteKind !== "invitation") {
      // Only keep details if at least one assignment field exists
      const hasAssignmentField = [...presentKeys].some(
        (key) =>
          ![
            "guestName",
            "prefix",
            "firstName",
            "middleName",
            "lastName",
            "email",
            "mobile",
            "regNo",
            "occasionName",
            "eventName",
            "itineraryUrl",
          ].includes(key)
      );
      if (hasAssignmentField) presentKeys.add("details");
      else presentKeys.delete("details");
      presentKeys.delete("itineraryUrl");
    } else {
      presentKeys.delete("details");
      presentKeys.delete("itineraryUrl");
      // strip assignment-only keys for invitation
      [
        "hotelName",
        "hotelAddress",
        "roomType",
        "checkInDate",
        "checkInTime",
        "checkOutDate",
        "checkOutTime",
        "noOfRooms",
        "occupancy",
        "stayStatus",
        "floorPreference",
        "roomFacing",
        "roomPreference",
        "specialRequirements",
        "arrivalDate",
        "arrivalTime",
        "arrivalMode",
        "arrivalFlightTrainBusNo",
        "arrivalPNRNo",
        "arrivalPickUpLocation",
        "pickupVehicle",
        "departureDate",
        "departureTime",
        "departureMode",
        "departureFlightTrainBusNo",
        "departurePNRNo",
        "departureDropLocation",
        "dropVehicle",
        "localVehicle",
        "itineraryNotes",
        "seatLabel",
        "tableNumber",
        "rowNumber",
        "seatNumber",
      ].forEach((key) => presentKeys.delete(key));
    }

    const preferredOrder = Object.keys(FIELD_LABELS);
    return preferredOrder
      .filter((key) => presentKeys.has(key))
      .map((key) => ({
        key,
        token: `{{${key}}}`,
        label: FIELD_LABELS[key] || key,
      }));
  }, [
    selectedContacts,
    filteredGuestWithAttendees,
    regNoMap,
    getPlaceholderValuesForContact,
    formData.occasionId,
    occasions,
    selectedEvent?.eventName,
    inviteKind,
    splitInviteByOccasion,
  ]);

  const getRecipientOccasionIds = useCallback(
    (recipient) => {
      if (formData.occasionId !== "all") {
        return getRsvpOccasionIds();
      }
      const allocKey = Object.entries(regNoMap).find(
        ([, reg]) => String(reg) === String(recipient?.regNo || "")
      )?.[0];
      if (allocKey && Array.isArray(selectedAllocations[allocKey])) {
        return selectedAllocations[allocKey];
      }
      return getRsvpOccasionIds();
    },
    [formData.occasionId, getRsvpOccasionIds, regNoMap, selectedAllocations]
  );

  const getQrPayload = useCallback(
    (regNo, occasionId = null) => {
      if (isPerOccasionQr && occasionId && occasionId !== "all") {
        return `${regNo}|${occasionId}`;
      }
      return String(regNo || "");
    },
    [isPerOccasionQr]
  );

  const getInvitationOccasionRows = useCallback(
    (occasionIds = []) => {
      const ids = (occasionIds || []).map(String).filter(Boolean);
      return ids
        .map((id) => occasions.find((o) => String(o._id) === id))
        .filter(Boolean)
        .map((occasion) => ({
          name: occasion.OccasionName || "",
          dateTime: formatWhatsAppDateTime(occasion.Date, occasion.time),
          venue: formatVenueFromOccasion(occasion),
          dressCode: String(occasion.DressCode || "").trim(),
        }));
    },
    [occasions]
  );

  const buildInvitationEventPassBlob = useCallback(
    async (contact, occasionIds = []) => {
      const ids = (occasionIds || []).map(String).filter(Boolean);
      const perOccasion = Boolean(isPerOccasionQr && ids.length === 1);
      const payload = getQrPayload(
        contact?.regNo,
        perOccasion ? ids[0] : null
      );
      if (!payload || payload === "undefined") {
        throw new Error("Registration number is required for the invitation.");
      }
      return generateInvitationCardBlob({
        guestName: contact?.name || "Guest",
        eventName: selectedEvent?.eventName || "our celebration",
        hostedBy: selectedEvent?.hostedBy || "",
        occasions: getInvitationOccasionRows(ids),
        qrPayload: payload,
      });
    },
    [
      getQrPayload,
      isPerOccasionQr,
      selectedEvent?.eventName,
      selectedEvent?.hostedBy,
      getInvitationOccasionRows,
    ]
  );

  const publishInvitationEventPass = useCallback(
    async (contact, occasionIds = []) => {
      const blob = await buildInvitationEventPassBlob(contact, occasionIds);
      const form = new FormData();
      const safeName = String(contact?.name || "Guest")
        .replace(/[^\w.\- ]+/g, "_")
        .slice(0, 40);
      form.append("pass", blob, `${safeName}-event-pass.png`);
      const res = await axios.post(publishWhatsAppEventPass(), form);
      const url = String(res.data?.url || "").trim();
      if (!url) {
        throw new Error("Could not publish the event pass for WhatsApp.");
      }
      return url;
    },
    [buildInvitationEventPassBlob]
  );

  const previewOccasionMails = useMemo(() => {
    if (!splitInviteByOccasion) return [];
    const ids = getRecipientOccasionIds(previewRecipient).map(String);
    return occasions.filter((o) => ids.includes(String(o._id)));
  }, [
    splitInviteByOccasion,
    getRecipientOccasionIds,
    previewRecipient,
    occasions,
  ]);

  useEffect(() => {
    if (!emailModal.isOpen || !splitInviteByOccasion) return;
    if (!previewOccasionMails.length) {
      setPreviewMailOccasionId("");
      return;
    }
    const stillValid = previewOccasionMails.some(
      (o) => String(o._id) === String(previewMailOccasionId)
    );
    if (!stillValid) {
      setPreviewMailOccasionId(String(previewOccasionMails[0]._id));
    }
  }, [
    emailModal.isOpen,
    splitInviteByOccasion,
    previewOccasionMails,
    previewMailOccasionId,
  ]);

  const buildEmailPayload = useCallback(
    (
      recipient,
      {
        forPreview = false,
        occasionIds,
        occasionName,
        qrOccasionId,
        detailsText = "",
      } = {}
    ) => {
      const includeRsvp = inviteKind === "invitation";
      const hasRegNo = Boolean(recipient?.regNo);
      const resolvedOccasionIds = occasionIds || getRsvpOccasionIds();
      const resolvedOccasionName =
        occasionName ||
        getOccasionNamesForIds(resolvedOccasionIds) ||
        getOccasionNames();

      const cardOccasionId =
        qrOccasionId ||
        (resolvedOccasionIds.length === 1 ? resolvedOccasionIds[0] : null);
      const assignmentCards =
        inviteKind === "itinerary"
          ? []
          : getAssignmentCardsForContact(recipient, cardOccasionId);
      const placeholderValues = getPlaceholderValuesForContact(recipient, {
        occasionName: resolvedOccasionName,
        occasionId: cardOccasionId,
        detailsText,
      });
      const subject = applyTokenPlaceholders(
        emailModal.subject,
        placeholderValues
      );
      // Prefer structured cards in the email body; keep {{details}} only if no cards.
      const contentSource =
        assignmentCards.length > 0
          ? String(emailModal.content || "").replace(
              /\{\{\s*details\s*\}\}/gi,
              ""
            )
          : emailModal.content;
      const content = applyTokenPlaceholders(contentSource, placeholderValues);
      const itineraryUrl =
        inviteKind === "itinerary"
          ? buildGuestItineraryUrl(
              selectedEvent?.eventId,
              recipient?.regNo || (forPreview ? "PREVIEW" : "")
            )
          : "";
      const itineraryHighlights =
        inviteKind === "itinerary"
          ? getItineraryHighlightsForContact(recipient)
          : null;

      const themeOccasionId = cardOccasionId;
      const emailTheme = getOccasionThemeForId(occasions, themeOccasionId);
      const occasionIndex = themeOccasionId
        ? Math.max(
            0,
            occasions.findIndex(
              (o) => String(o._id) === String(themeOccasionId)
            )
          )
        : 0;

      const qrPayload = getQrPayload(
        recipient.regNo,
        qrOccasionId ||
          (isPerOccasionQr && resolvedOccasionIds.length === 1
            ? resolvedOccasionIds[0]
            : null)
      );

      return {
        to: [
          {
            email: recipient.email,
            name: recipient.name,
            regNo: recipient.regNo,
            qrPayload,
            guestId: recipient.guestId || recipient.id,
            attendeeId:
              recipient.type === "Attendee" ? recipient.id : null,
          },
        ],
        subject,
        content,
        footer: emailModal.footer,
        eventName: selectedEvent?.eventName || "",
        eventId: selectedEvent?.eventId || "",
        occasionNames: resolvedOccasionName,
        occasionIds: resolvedOccasionIds,
        qrPayload,
        emailTheme,
        occasionIndex,
        emailKind: inviteKind || "invitation",
        assignmentCards,
        includeRsvp,
        itineraryUrl,
        itineraryHighlights,
        attachMedia: Boolean(emailModal.attachMedia),
        addQR: Boolean(
          emailModal.addQR &&
            inviteKind !== "itinerary" &&
            (forPreview || hasRegNo)
        ),
        occasionDetails:
          inviteKind === "invitation"
            ? getInvitationOccasionRows(resolvedOccasionIds)
            : [],
        invitationCardUrl:
          inviteKind === "invitation" ? emailInviteCardUrl || "" : "",
        mediaItems: emailModal.attachMedia
          ? getMediaItemsForOccasionIds(resolvedOccasionIds)
          : [],
        attachments: emailDeviceFiles
          .filter((file) => file.content)
          .map((file) => ({
            filename: file.filename || file.name,
            content: file.content,
            type: file.mimeType || "application/octet-stream",
          })),
      };
    },
    [
      inviteKind,
      emailModal.subject,
      emailModal.content,
      emailModal.footer,
      emailModal.attachMedia,
      emailModal.addQR,
      emailDeviceFiles,
      emailInviteCardUrl,
      selectedEvent?.eventName,
      selectedEvent?.eventId,
      getOccasionNames,
      getOccasionNamesForIds,
      getRsvpOccasionIds,
      getMediaItemsForOccasionIds,
      getQrPayload,
      occasions,
      isPerOccasionQr,
      getPlaceholderValuesForContact,
      getAssignmentCardsForContact,
      getItineraryHighlightsForContact,
      getInvitationOccasionRows,
    ]
  );

  const getAllAllocatedOccasionIds = useCallback(
    (recipient) => {
      const allocKey = Object.entries(regNoMap).find(
        ([, reg]) => String(reg) === String(recipient?.regNo || "")
      )?.[0];
      if (allocKey && Array.isArray(selectedAllocations[allocKey])) {
        return selectedAllocations[allocKey].map(String);
      }
      return occasions.map((o) => String(o._id));
    },
    [regNoMap, selectedAllocations, occasions]
  );

  const insertDynamicField = (token) => {
    if (!token) return;
    const field = dynamicFieldTarget === "subject" ? "subject" : "content";
    const inputEl =
      field === "subject" ? subjectInputRef.current : contentInputRef.current;
    const current = emailModal[field] || "";
    let nextValue = `${current}${token}`;

    if (inputEl && typeof inputEl.selectionStart === "number") {
      const start = inputEl.selectionStart;
      const end = inputEl.selectionEnd;
      nextValue = `${current.slice(0, start)}${token}${current.slice(end)}`;
      setEmailModal((prev) => ({ ...prev, [field]: nextValue }));
      requestAnimationFrame(() => {
        inputEl.focus();
        const cursor = start + token.length;
        inputEl.setSelectionRange(cursor, cursor);
      });
    } else {
      setEmailModal((prev) => ({ ...prev, [field]: nextValue }));
    }

    try {
      navigator.clipboard?.writeText(token);
    } catch {
      /* ignore clipboard failures */
    }
    toast.success(`Inserted ${token} into ${field}`);
  };

  const handleSendEmail = async ({ sendAllOccasions = false } = {}) => {
    if (hasNoAssignments) {
      toast.error(
        `${emptyAssignmentCopy.title}. ${emptyAssignmentCopy.body}`
      );
      return null;
    }

    if (!emailModal.subject || !emailModal.content) {
      toast.error("Please enter subject and content before sending.");
      return null;
    }

    const recipients = selectedContacts
      .filter((c) => c.email && c.regNo)
      .map((c) => ({
        id: c.id,
        guestId: c.guestId,
        type: c.type,
        email: c.email,
        name: c.name,
        regNo: c.regNo,
      }));

    if (recipients.length === 0) {
      toast.error("No valid email addresses with RegNo selected.");
      return null;
    }

    let successCount = 0;
    let failedCount = 0;

    for (const recipient of recipients) {
      let occasionIds = [];

      if (sendAllOccasions || splitInviteByOccasion) {
        const allocated = getAllAllocatedOccasionIds(recipient);
        if (sendAllOccasions) {
          occasionIds = allocated;
        } else if (previewMailOccasionId) {
          occasionIds = allocated.includes(String(previewMailOccasionId))
            ? [String(previewMailOccasionId)]
            : [];
        } else {
          occasionIds = allocated;
        }
      } else {
        occasionIds = getRecipientOccasionIds(recipient).map(String);
      }

      if (!occasionIds.length) {
        failedCount += 1;
        continue;
      }

      // Different QR: one email per allocated occasion.
      // Same QR: one email listing all target occasions.
      const shouldSplit =
        inviteKind === "seat" ||
        (inviteKind === "invitation" && isPerOccasionQr);

      const withInvitationCard = async (payload, ids) => {
        if (inviteKind !== "invitation") return payload;
        try {
          const blob = await buildInvitationEventPassBlob(recipient, ids);
          const content = await blobToBase64(blob);
          const form = new FormData();
          form.append("pass", blob, "invitation-card.png");
          const published = await axios.post(publishWhatsAppEventPass(), form);
          payload.invitationCardUrl = String(published.data?.url || "");
          payload.attachments = [
            ...(payload.attachments || []),
            {
              filename: "Invitation-Card.png",
              content,
              type: "image/png",
            },
          ];
        } catch (err) {
          console.error("Invitation card attach failed:", err);
        }
        return payload;
      };

      if (shouldSplit) {
        for (const occasionId of occasionIds) {
          const occasion = occasions.find(
            (o) => String(o._id) === String(occasionId)
          );
          try {
            const payload = await withInvitationCard(
              buildEmailPayload(recipient, {
                occasionIds: [occasionId],
                occasionName: occasion?.OccasionName || "",
                qrOccasionId: isPerOccasionQr ? occasionId : null,
                detailsText: getDetailsTextForContact(recipient, occasionId),
              }),
              [occasionId]
            );
            await axios.post(sendEmailInvite(), payload);
            successCount += 1;
          } catch (err) {
            console.error("❌ Email send error:", err);
            failedCount += 1;
          }
        }
      } else {
        try {
          const payload = await withInvitationCard(
            buildEmailPayload(recipient, {
              occasionIds,
              occasionName: getOccasionNamesForIds(occasionIds),
              qrOccasionId: null,
              detailsText: getDetailsTextForContact(recipient),
            }),
            occasionIds
          );
          await axios.post(sendEmailInvite(), payload);
          successCount += 1;
        } catch (err) {
          console.error("❌ Email send error:", err);
          failedCount += 1;
        }
      }
    }

    return { successCount, failedCount };
  };

  useEffect(() => {
    if (
      Array.isArray(filteredGuestWithAttendees) &&
      Object.keys(selectedRows).length === 0
    ) {
      const initialSelections = {};
      filteredGuestWithAttendees.forEach((entry) => {
        if (entry.guest) {
          initialSelections[`guest-${entry.guest._id}`] = true;
        }
        entry.attendees?.forEach((attendee) => {
          initialSelections[`attendee-${attendee._id}`] = true;
        });
      });
      setSelectedRows(initialSelections);
    }
  }, [filteredGuestWithAttendees, selectedRows]);

  const handlePreviewClick = () => {
    if (hasNoAssignments) {
      toast.error(
        `${emptyAssignmentCopy.title}. ${emptyAssignmentCopy.body}`
      );
      return;
    }

    const latestQrMode = readEventQrMode(selectedEvent?.eventId);
    setQrMode(latestQrMode);
    const splitByOccasion =
      inviteKind === "invitation" &&
      formData.occasionId === "all" &&
      latestQrMode === QR_MODE_PER_OCCASION;
    const occasionNames = getOccasionNames();
    const eventName = selectedEvent?.eventName || "the event";
    let defaultSubject = splitByOccasion
      ? `You are cordially invited to {{occasionName}} at ${eventName}`
      : `You are cordially invited to ${occasionNames} at ${eventName}`;
    let defaultContent = splitByOccasion
      ? `Dear {{guestName}},

You are cordially invited to attend {{occasionName}}.

Please carry the QR code below during the event for verification.

Kindly confirm your attendance using the RSVP options below.

We look forward to your presence!`
      : `Dear {{guestName}},

You are cordially invited to attend the following occasions.

Please carry the QR code below during the event for verification.

Kindly confirm your attendance using the RSVP options below.

We look forward to your presence!`;
    let addQR = true;

    if (inviteKind === "accommodation") {
      defaultSubject = `Your accommodation details for ${eventName}`;
      defaultContent = `Dear {{guestName}},

Please find your stay arrangements for ${eventName} below.

If you have any questions about your accommodation, reply to this email or contact the event host.

We look forward to hosting you!`;
      addQR = false;
    } else if (inviteKind === "travel") {
      defaultSubject = `Your travel details for ${eventName}`;
      defaultContent = `Dear {{guestName}},

Please find your travel arrangements for ${eventName} below.

If you need any changes, reply to this email or contact the event host.

Safe travels!`;
      addQR = false;
    } else if (inviteKind === "seat") {
      defaultSubject = `Your seating details for ${
        formData.occasionId === "all"
          ? "{{occasionName}}"
          : occasionNames || eventName
      }`;
      defaultContent = `Dear {{guestName}},

Your seat allotment for the event is shown below by occasion.

Please keep this email for reference when you arrive at the venue.

We look forward to seeing you!`;
      addQR = false;
    } else if (inviteKind === "itinerary") {
      defaultSubject = `Your itinerary for ${eventName}`;
      defaultContent = `Dear {{guestName}},

Thank you for confirming your attendance at {{eventName}}.

Your personalized guest itinerary is ready.`;
      addQR = false;
    }

    const defaultFooter =
      inviteKind === "itinerary"
        ? `Warm regards,\nFestiva Team`
        : `Warm regards,\nTecogis Team`;

    const initialPreviewOccasion = splitByOccasion
      ? String(getRecipientOccasionIds(previewRecipient)[0] || "")
      : formData.occasionId !== "all"
        ? String(formData.occasionId)
        : "";

    setPreviewMailOccasionId(initialPreviewOccasion);
    setEmailPreviewHtml("");
    emailPreviewHtmlRef.current = "";
    setEmailPreviewDevice("desktop");
    setEmailInviteCardUrl("");
    clearEmailDeviceFiles();
    setEmailModal({
      isOpen: true,
      subject: defaultSubject,
      content: defaultContent,
      footer: defaultFooter,
      attachMedia: false,
      addQR,
    });
  };

  useEffect(() => {
    if (!emailModal.isOpen) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!emailPreviewHtmlRef.current) setEmailPreviewLoading(true);
      try {
        const recipient = {
          id: previewRecipient.id,
          guestId: previewRecipient.guestId,
          type: previewRecipient.type,
          email: previewRecipient.email || "preview@example.com",
          name: previewRecipient.name || "Guest",
          firstName: previewRecipient.firstName || "",
          prefix: previewRecipient.prefix || "",
          middleName: previewRecipient.middleName || "",
          lastName: previewRecipient.lastName || "",
          regNo: previewRecipient.regNo || "PREVIEW",
        };

        let payloadOptions = {
          forPreview: true,
          detailsText: getDetailsTextForContact(
            recipient,
            formData.occasionId !== "all" ? formData.occasionId : null
          ),
        };

        if (splitInviteByOccasion && previewMailOccasionId) {
          const occasion = occasions.find(
            (o) => String(o._id) === String(previewMailOccasionId)
          );
          payloadOptions = {
            forPreview: true,
            occasionIds: [previewMailOccasionId],
            occasionName: occasion?.OccasionName || "",
            qrOccasionId: isPerOccasionQr ? previewMailOccasionId : null,
            detailsText: getDetailsTextForContact(
              recipient,
              previewMailOccasionId
            ),
          };
        } else if (
          inviteKind === "invitation" &&
          formData.occasionId === "all" &&
          !isPerOccasionQr
        ) {
          const allocatedIds = getRecipientOccasionIds(recipient).map(String);
          payloadOptions = {
            forPreview: true,
            occasionIds: allocatedIds,
            occasionName: getOccasionNamesForIds(allocatedIds),
            qrOccasionId: null,
            detailsText: getDetailsTextForContact(recipient),
          };
        } else if (
          isPerOccasionQr &&
          formData.occasionId !== "all" &&
          formData.occasionId
        ) {
          payloadOptions.qrOccasionId = formData.occasionId;
          payloadOptions.occasionIds = [formData.occasionId];
        }

        const res = await axios.post(
          previewEmailInvite(),
          buildEmailPayload(recipient, payloadOptions)
        );
        if (cancelled) return;
        const html = res.data.html || "";
        emailPreviewHtmlRef.current = html;
        setEmailPreviewHtml(html);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load email preview:", error);
        if (!emailPreviewHtmlRef.current) setEmailPreviewHtml("");
      } finally {
        if (!cancelled) setEmailPreviewLoading(false);
      }
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    emailModal.isOpen,
    previewRecipient,
    buildEmailPayload,
    splitInviteByOccasion,
    isPerOccasionQr,
    previewMailOccasionId,
    occasions,
    getDetailsTextForContact,
    getRecipientOccasionIds,
    getOccasionNamesForIds,
    inviteKind,
    formData.occasionId,
  ]);

  useEffect(() => {
    if (!emailModal.isOpen || inviteKind !== "invitation") {
      return undefined;
    }
    const recipient = {
      ...(previewRecipient || {}),
      name: previewRecipient?.name || "Guest",
      regNo: previewRecipient?.regNo || "PREVIEW",
    };
    const occasionIds =
      splitInviteByOccasion && previewMailOccasionId
        ? [String(previewMailOccasionId)]
        : getRecipientOccasionIds(recipient).map(String);

    let cancelled = false;
    (async () => {
      try {
        const blob = await buildInvitationEventPassBlob(recipient, occasionIds);
        const dataUrl = await blobToDataUrl(blob);
        if (!cancelled) setEmailInviteCardUrl(dataUrl);
      } catch (err) {
        console.error("Email invitation card preview failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    emailModal.isOpen,
    inviteKind,
    previewRecipient?.id,
    previewRecipient?.regNo,
    previewRecipient?.name,
    splitInviteByOccasion,
    previewMailOccasionId,
    formData.occasionId,
    buildInvitationEventPassBlob,
    getRecipientOccasionIds,
  ]);

  const handleCheckboxChange = (key) => {
    setSelectedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 🔹 Compute counts after filteredGuestWithAttendees is ready
  const totalGuests = filteredGuestWithAttendees.filter(
    (entry) => entry.guest
  ).length;
  const totalAttendees = filteredGuestWithAttendees.reduce((acc, entry) => {
    return acc + (entry.attendees ? entry.attendees.length : 0);
  }, 0);
  const TotalGuestCount = totalGuests + totalAttendees;

  const buildWhatsAppPayloadForContact = useCallback(
    (contact, occasionId = null, occasionIdsOverride = null) => {
      const occasion =
        occasions.find((o) => String(o._id) === String(occasionId)) ||
        (formData.occasionId && formData.occasionId !== "all"
          ? occasions.find((o) => String(o._id) === String(formData.occasionId))
          : null) ||
        occasions[0] ||
        null;

      const guestName =
        inviteKind === "itinerary"
          ? contact?.firstName || contact?.name || "Guest"
          : contact?.name || "Guest";
      const eventName = selectedEvent?.eventName || "";
      const hostTeam = formatHostContact(occasion, {
        hostedBy: selectedEvent?.hostedBy,
        eventName,
      });
      const base = {
        inviteKind,
        mobile: contact?.mobile || "",
        guestName,
        eventName,
        eventId: selectedEvent?.eventId || "",
        guestId: contact?.guestId || (contact?.type === "Guest" ? contact?.id : "") || "",
        attendeeId: contact?.type === "Attendee" ? contact?.id : null,
        regNo: contact?.regNo || "",
        hostTeam,
      };

      if (inviteKind === "invitation") {
        const allocated = getAllAllocatedOccasionIds(contact).map(String);
        const occasionIds = Array.isArray(occasionIdsOverride)
          ? occasionIdsOverride.map(String).filter((id) =>
              allocated.length ? allocated.includes(id) : true
            )
          : occasionId
            ? allocated.filter((id) => id === String(occasionId))
            : formData.occasionId && formData.occasionId !== "all"
              ? allocated.filter((id) => id === String(formData.occasionId))
              : allocated;
        const selectedOccasions = occasionIds
          .map((id) => occasions.find((o) => String(o._id) === String(id)))
          .filter(Boolean);
        const details = formatInvitationOccasionDetails(selectedOccasions);
        return {
          ...base,
          occasionIds,
          occasionNames: details.occasionNames || getOccasionNames(),
          dateTime: details.dateTime,
          venue: details.venue,
          headerMediaUrl: "",
          contactPerson: hostTeam,
        };
      }

      if (inviteKind === "accommodation") {
        const values = getAssignmentFieldValues(contact);
        return {
          ...base,
          hotelName: values.hotelName || "",
          hotelAddress: values.hotelAddress || "",
          checkIn: formatWhatsAppDateTime(
            values.checkInDate,
            values.checkInTime
          ),
          checkOut: formatWhatsAppDateTime(
            values.checkOutDate,
            values.checkOutTime
          ),
          roomLabel: values.roomLabel || values.roomType || "",
          contactPerson: hostTeam,
          contactMobile:
            occasion?.hostname?.mobileNo ||
            String(selectedEvent?.hostedBy || ""),
        };
      }

      if (inviteKind === "travel") {
        const values = getAssignmentFieldValues(contact);
        const trips = [];
        if (values._pickupApplicable) {
          trips.push({
            tripType: "Pickup",
            dateTime: formatWhatsAppDateTime(
              values.pickupDate || values.arrivalDate,
              values.pickupTime || values.arrivalTime
            ),
            location: values.pickupLocation || values.arrivalPickUpLocation || "",
            vehicle: values.pickupVehicle || values.localVehicle || "",
            driver: [values.pickupChauffeur, values.pickupDriverMobile]
              .filter(Boolean)
              .join(", "),
          });
        }
        if (values._dropApplicable) {
          trips.push({
            tripType: "Drop",
            dateTime: formatWhatsAppDateTime(
              values.dropDate || values.departureDate,
              values.dropTime || values.departureTime
            ),
            location: values.dropLocation || values.departureDropLocation || "",
            vehicle: values.dropVehicle || values.localVehicle || "",
            driver: [values.dropChauffeur, values.dropDriverMobile]
              .filter(Boolean)
              .join(", "),
          });
        }
        if (!trips.length) {
          trips.push({
            tripType: "Travel",
            dateTime: formatWhatsAppDateTime(
              values.arrivalDate || values.departureDate,
              values.arrivalTime || values.departureTime
            ),
            location:
              values.arrivalPickUpLocation ||
              values.departureDropLocation ||
              "",
            vehicle: values.localVehicle || values.pickupVehicle || values.dropVehicle || "",
            driver: "",
          });
        }
        return { ...base, trips };
      }

      if (inviteKind === "seat") {
        const values = getAssignmentFieldValues(contact, occasionId);
        const seatOccasion =
          occasions.find((o) => String(o._id) === String(occasionId)) ||
          occasion;
        return {
          ...base,
          occasionName: seatOccasion?.OccasionName || getOccasionNames(),
          seatLabel: values.seatLabel || formatSeatLabel({
            tableAllocation: {
              tableNumber: values.tableNumber,
              seatNumber: values.seatNumber,
            },
            rowAllocation: {
              rowNumber: values.rowNumber,
              seatNumber: values.seatNumber,
            },
          }) || "Assigned",
          hostTeam,
        };
      }

      if (inviteKind === "itinerary") {
        const highlights = getItineraryHighlightsForContact(contact);
        const parts = [];
        if (highlights?.occasions?.length) parts.push("your event schedule");
        if (highlights?.stay?.length) parts.push("accommodation");
        if (highlights?.travel?.length) parts.push("travel arrangements");
        if (highlights?.seats?.length) parts.push("seating information");
        return {
          ...base,
          itineraryDetails:
            parts.length > 0
              ? parts.join(", ").replace(/,([^,]*)$/, ", and$1")
              : "your event schedule, accommodation, travel arrangements, and seating information",
        };
      }

      return base;
    },
    [
      inviteKind,
      occasions,
      formData.occasionId,
      selectedEvent,
      getAssignmentFieldValues,
      getMediaItemsForOccasionIds,
      getOccasionNames,
      getRecipientOccasionIds,
      getAllAllocatedOccasionIds,
      getItineraryHighlightsForContact,
    ]
  );

  const whatsappPreviewGuests = useMemo(() => {
    const withMobile = selectedContacts.filter(
      (c) => c.mobile && String(c.mobile).replace(/\D/g, "").length >= 10
    );
    if (withMobile.length) return withMobile;
    if (selectedContacts.length) return selectedContacts;
    return [
      {
        name: "Guest",
        email: "",
        mobile: "",
        regNo: "PREVIEW",
      },
    ];
  }, [selectedContacts]);

  const handleWhatsAppPreviewClick = () => {
    if (!formData.invitationType) {
      toast.error(
        needsOccasion
          ? "Please select both Occasion and Invitation Type."
          : "Please select Invitation Type."
      );
      return;
    }
    if (needsOccasion && !formData.occasionId) {
      toast.error("Please select both Occasion and Invitation Type.");
      return;
    }
    if (hasNoAssignments) {
      toast.error(
        `${emptyAssignmentCopy.title}. ${emptyAssignmentCopy.body}`
      );
      return;
    }
    const latestQrMode = readEventQrMode(selectedEvent?.eventId);
    setQrMode(latestQrMode);

    const firstGuest =
      selectedContacts.find(
        (c) => c.mobile && String(c.mobile).replace(/\D/g, "").length >= 10
      ) ||
      selectedContacts[0] ||
      null;
    const splitByOccasion =
      inviteKind === "invitation" &&
      formData.occasionId === "all" &&
      latestQrMode === QR_MODE_PER_OCCASION;
    const initialOccasion = splitByOccasion
      ? String(getRecipientOccasionIds(firstGuest)[0] || "")
      : formData.occasionId !== "all"
        ? String(formData.occasionId)
        : "";

    setWhatsappPreviewGuestIndex(0);
    setPreviewWhatsAppOccasionId(initialOccasion);
    setWhatsappModalOpen(true);
  };

  const whatsappPreviewGuest =
    whatsappPreviewGuests[
      Math.min(
        whatsappPreviewGuestIndex,
        Math.max(whatsappPreviewGuests.length - 1, 0)
      )
    ] || whatsappPreviewGuests[0];

  const whatsappPreviewData = useMemo(() => {
    if (!whatsappModalOpen) return null;

    const contact = {
      ...(whatsappPreviewGuest || {}),
      name: whatsappPreviewGuest?.name || "Guest",
      regNo: whatsappPreviewGuest?.regNo || "PREVIEW",
      mobile: whatsappPreviewGuest?.mobile || "",
    };
    const occasionIdForPreview =
      inviteKind === "seat"
        ? formData.occasionId === "all"
          ? String(getRecipientOccasionIds(contact)[0] || "")
          : String(formData.occasionId || "")
        : inviteKind === "invitation" && isPerOccasionQr
          ? previewWhatsAppOccasionId ||
            (formData.occasionId !== "all"
              ? String(formData.occasionId)
              : "")
          : null;
    const combinedOccasionIds =
      inviteKind === "invitation" && !isPerOccasionQr
        ? getRecipientOccasionIds(contact).map(String)
        : null;
    const payload = buildWhatsAppPayloadForContact(
      contact,
      occasionIdForPreview || null,
      combinedOccasionIds
    );
    if (
      inviteKind === "invitation" &&
      !payload.occasionIds?.length &&
      occasionIdForPreview
    ) {
      payload.occasionIds = [String(occasionIdForPreview)];
    }
    return buildLocalWhatsAppPreview(payload);
  }, [
    whatsappModalOpen,
    whatsappPreviewGuest,
    inviteKind,
    formData.occasionId,
    previewWhatsAppOccasionId,
    isPerOccasionQr,
    buildWhatsAppPayloadForContact,
    getRecipientOccasionIds,
  ]);

  const whatsappPreviewPassKey = useMemo(() => {
    if (!whatsappModalOpen || inviteKind !== "invitation") return "";
    const guest = whatsappPreviewGuest || {};
    const occasionPart = isPerOccasionQr
      ? String(
          previewWhatsAppOccasionId ||
            (formData.occasionId !== "all" ? formData.occasionId : "") ||
            ""
        )
      : "all";
    return [
      guest.id || "",
      guest.regNo || "",
      guest.name || "",
      occasionPart,
    ].join("|");
  }, [
    whatsappModalOpen,
    inviteKind,
    isPerOccasionQr,
    whatsappPreviewGuest?.id,
    whatsappPreviewGuest?.regNo,
    whatsappPreviewGuest?.name,
    previewWhatsAppOccasionId,
    formData.occasionId,
  ]);

  useEffect(() => {
    if (!whatsappPreviewPassKey) return undefined;
    const cached = whatsappPassCacheRef.current[whatsappPreviewPassKey];
    if (cached) {
      setWhatsappEventPassUrl(cached);
      return undefined;
    }

    const contact = {
      ...(whatsappPreviewGuest || {}),
      name: whatsappPreviewGuest?.name || "Guest",
      regNo: whatsappPreviewGuest?.regNo || "PREVIEW",
    };
    const occasionIds = isPerOccasionQr
      ? [
          previewWhatsAppOccasionId ||
            (formData.occasionId !== "all"
              ? String(formData.occasionId)
              : String(getRecipientOccasionIds(contact)[0] || "")),
        ].filter(Boolean)
      : getRecipientOccasionIds(contact).map(String);

    let cancelled = false;
    (async () => {
      try {
        const blob = await buildInvitationEventPassBlob(contact, occasionIds);
        const dataUrl = await blobToDataUrl(blob);
        whatsappPassCacheRef.current[whatsappPreviewPassKey] = dataUrl;
        if (!cancelled) setWhatsappEventPassUrl(dataUrl);
      } catch (err) {
        console.error("Event pass preview failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Guest/occasion identity is encoded in whatsappPreviewPassKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappPreviewPassKey]);

  useEffect(() => {
    if (!whatsappModalOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [whatsappModalOpen]);

  useEffect(() => {
    if (whatsappPreviewGuestIndex >= whatsappPreviewGuests.length) {
      setWhatsappPreviewGuestIndex(Math.max(0, whatsappPreviewGuests.length - 1));
    }
  }, [whatsappPreviewGuests.length, whatsappPreviewGuestIndex]);

  const previewWhatsAppOccasions = useMemo(() => {
    if (!splitInviteByOccasion) return [];
    const ids = getRecipientOccasionIds(whatsappPreviewGuest).map(String);
    return occasions.filter((o) => ids.includes(String(o._id)));
  }, [
    splitInviteByOccasion,
    getRecipientOccasionIds,
    whatsappPreviewGuest,
    occasions,
  ]);

  useEffect(() => {
    if (!whatsappModalOpen || !splitInviteByOccasion) return;
    if (!previewWhatsAppOccasions.length) {
      setPreviewWhatsAppOccasionId("");
      return;
    }
    const stillValid = previewWhatsAppOccasions.some(
      (o) => String(o._id) === String(previewWhatsAppOccasionId)
    );
    if (!stillValid) {
      setPreviewWhatsAppOccasionId(String(previewWhatsAppOccasions[0]._id));
    }
  }, [
    whatsappModalOpen,
    splitInviteByOccasion,
    previewWhatsAppOccasions,
    previewWhatsAppOccasionId,
  ]);

  const handleSendWhatsAppAndInvites = async ({
    sendAllOccasions = false,
  } = {}) => {
    if (whatsappSending) return;
    if (!formData.invitationType) {
      toast.error(
        needsOccasion
          ? "Please select both Occasion and Invitation Type."
          : "Please select Invitation Type."
      );
      return;
    }
    if (needsOccasion && !formData.occasionId) {
      toast.error("Please select both Occasion and Invitation Type.");
      return;
    }
    if (hasNoAssignments) {
      toast.error(
        `${emptyAssignmentCopy.title}. ${emptyAssignmentCopy.body}`
      );
      return;
    }

    const recipients = selectedContacts.filter(
      (c) => c.mobile && String(c.mobile).replace(/\D/g, "").length >= 10
    );
    if (!recipients.length) {
      toast.error("No selected guests with a valid mobile number.");
      return;
    }

    setWhatsappSending(true);
    let successCount = 0;
    let failedCount = 0;
    const recipientMessageMap = {};

    try {
      for (const contact of recipients) {
        try {
          const messageIds = [];

          if (inviteKind === "seat") {
            const occasionIds =
              formData.occasionId === "all"
                ? getRecipientOccasionIds(contact).map(String)
                : [String(formData.occasionId)];
            if (!occasionIds.length) {
              failedCount += 1;
              continue;
            }
            for (const occasionId of occasionIds) {
              const payload = buildWhatsAppPayloadForContact(
                contact,
                occasionId
              );
              const res = await axios.post(sendWhatsAppInvite(), payload);
              const ids = res.data?.data?.messageIds || [];
              if (res.data?.data?.messageId) ids.push(res.data.data.messageId);
              messageIds.push(...ids.filter(Boolean));
            }
          } else if (inviteKind === "invitation") {
            const allocated = (
              sendAllOccasions
                ? getAllAllocatedOccasionIds(contact)
                : getRecipientOccasionIds(contact)
            ).map(String);
            const latestQrMode = readEventQrMode(selectedEvent?.eventId);
            const perOccasionQr = latestQrMode === QR_MODE_PER_OCCASION;
            const selectedOccasionId = sendAllOccasions
              ? null
              : previewWhatsAppOccasionId ||
                (formData.occasionId !== "all"
                  ? String(formData.occasionId)
                  : "");
            const targetIds = sendAllOccasions
              ? allocated
              : selectedOccasionId
                ? allocated.filter((id) => id === String(selectedOccasionId))
                : allocated;

            if (!targetIds.length) {
              failedCount += 1;
              continue;
            }

            // Different QR: one WhatsApp per allocated occasion.
            // Same QR: one WhatsApp listing the target occasion(s).
            if (perOccasionQr) {
              for (const occasionId of targetIds) {
                const payload = buildWhatsAppPayloadForContact(
                  contact,
                  occasionId
                );
                if (!payload.occasionIds?.length) continue;
                payload.headerMediaUrl = await publishInvitationEventPass(
                  contact,
                  [occasionId]
                );
                const res = await axios.post(sendWhatsAppInvite(), payload);
                const ids = res.data?.data?.messageIds || [];
                if (res.data?.data?.messageId) ids.push(res.data.data.messageId);
                messageIds.push(...ids.filter(Boolean));
              }
              if (!messageIds.length) {
                failedCount += 1;
                continue;
              }
            } else {
              const payload = buildWhatsAppPayloadForContact(
                contact,
                null,
                targetIds
              );
              if (!payload.occasionIds?.length) {
                failedCount += 1;
                continue;
              }
              payload.headerMediaUrl = await publishInvitationEventPass(
                contact,
                targetIds
              );
              const res = await axios.post(sendWhatsAppInvite(), payload);
              const ids = res.data?.data?.messageIds || [];
              if (res.data?.data?.messageId) ids.push(res.data.data.messageId);
              messageIds.push(...ids.filter(Boolean));
            }
          } else {
            const payload = buildWhatsAppPayloadForContact(contact);
            const res = await axios.post(sendWhatsAppInvite(), payload);
            const ids = res.data?.data?.messageIds || [];
            if (res.data?.data?.messageId) ids.push(res.data.data.messageId);
            messageIds.push(...ids.filter(Boolean));
          }

          recipientMessageMap[contact.id] = messageIds[0] || null;
          successCount += 1;
        } catch (err) {
          console.error("WhatsApp send failed for", contact.name, err);
          failedCount += 1;
          const detail =
            err?.response?.data?.message ||
            err?.response?.data?.error?.message ||
            (typeof err?.response?.data?.error === "string"
              ? err.response.data.error
              : "") ||
            err?.message;
          if (detail) {
            toast.error(`${contact.name || "Guest"}: ${detail}`);
          }
        }
      }

      if (successCount > 0) {
        await handleSendInvites(recipientMessageMap, {
          forceAllOccasions: sendAllOccasions,
          selectedOccasionId: sendAllOccasions
            ? null
            : previewWhatsAppOccasionId ||
              (formData.occasionId !== "all" ? formData.occasionId : null),
          quiet: true,
        });
      }

      if (successCount > 0 && failedCount === 0) {
        toast.success(
          `${successCount} WhatsApp message(s) queued with Interakt.`
        );
        setWhatsappModalOpen(false);
      } else if (successCount > 0 && failedCount > 0) {
        toast.error(
          `Queued ${successCount} WhatsApp message(s), failed ${failedCount}.`
        );
      } else {
        toast.error(
          "Failed to send WhatsApp messages. Check occasion media URL is public (not localhost)."
        );
      }
    } finally {
      setWhatsappSending(false);
    }
  };

  const handleSendInvites = async (
    recipientMessageMap = {},
    {
      forceAllOccasions = false,
      selectedOccasionId = null,
      quiet = false,
    } = {}
  ) => {
    if (!formData.invitationType) {
      toast.error(
        needsOccasion
          ? "Please select both Occasion and Invitation Type."
          : "Please select Invitation Type."
      );
      return;
    }
    if (needsOccasion && !formData.occasionId) {
      toast.error("Please select both Occasion and Invitation Type.");
      return;
    }

    try {
      const payloads = [];

      filteredGuestWithAttendees.forEach((entry) => {
        const mainGuest = entry.guest;
        const attendees = entry.attendees || [];

        // helper to build new invites
        const buildInvitations = (occasionIds, recipientId) => {
          const now = new Date().toISOString();
          const eventLevel =
            inviteKind === "accommodation" ||
            inviteKind === "travel" ||
            inviteKind === "itinerary";
          const rows = eventLevel
            ? [null]
            : (occasionIds || []).filter(Boolean);
          return rows.map((occId) => {
            const messageId =
              formData.invitationType === "Email" ||
              formData.invitationType === "WhatsApp"
                ? recipientMessageMap[recipientId] || null
                : null;

            return {
              type: formData.invitationType,
              inviteKind: inviteKind || "invitation",
              ...(occId ? { occasionId: occId } : {}),
              sendDate: now,
              receivedDate:
                formData.invitationType === "Email" ||
                formData.invitationType === "WhatsApp"
                  ? now
                  : null,
              status: "sent",
              remarks: "",
              messageId,
            };
          });
        };

        const resolveOccasionIdsForInviteLog = (allocatedOccasions) => {
          const allocated = (allocatedOccasions || [])
            .map(String)
            .filter(Boolean);
          const eventLevel =
            inviteKind === "accommodation" ||
            inviteKind === "travel" ||
            inviteKind === "itinerary";

          if (eventLevel) return ["*"];

          if (forceAllOccasions) return allocated;
          if (selectedOccasionId) {
            return allocated.includes(String(selectedOccasionId)) ||
              allocatedOccasions.includes(selectedOccasionId)
              ? [selectedOccasionId]
              : [];
          }
          if (formData.occasionId === "all") return allocated;
          return formData.occasionId ? [formData.occasionId] : [];
        };

        // ---- MAIN GUEST ----
        if (mainGuest && selectedRows[`guest-${mainGuest._id}`]) {
          const mainKey = `${mainGuest._id}-null`;
          const allocatedOccasions = selectedAllocations[mainKey] || [];
          const occasionIds = resolveOccasionIdsForInviteLog(allocatedOccasions);

          if (occasionIds.length > 0) {
            const existingKeys = (mainGuest.invitations || []).map(
              (inv) =>
                `${inv.type}-${inv.inviteKind || "invitation"}-${
                  inv.occasionId?.toString?.() || inv.occasionId
                }`
            );

            const missingOccasions = occasionIds.filter(
              (occId) =>
                !existingKeys.includes(
                  `${formData.invitationType}-${inviteKind || "invitation"}-${occId}`
                )
            );

            if (missingOccasions.length > 0) {
              payloads.push({
                eventId: selectedEvent?.eventId,
                guestId: mainGuest._id,
                attendeeId: null,
                invitedOccasionIds: missingOccasions.filter((id) =>
                  /^[a-fA-F0-9]{24}$/.test(String(id))
                ),
                invitations: buildInvitations(missingOccasions, mainGuest._id),
                demographic: "Adult",
                gender: mainGuest.gender || "",
                foodPreference: mainGuest.foodPreference || "",
                allergic: mainGuest.allergic || "",
                specialNeeds: mainGuest.specialNeeds || "",
                specialRequest: mainGuest.specialRequest || "",
                enteredBy: eventEntity?.username || "admin",
                modifiedBy: eventEntity?.username || "admin",
              });
            }
          }
        }

        // ---- ATTENDEES ----
        attendees.forEach((attendee) => {
          if (!selectedRows[`attendee-${attendee._id}`]) return;

          const key = `${mainGuest?._id}-${attendee._id}`;
          const allocatedOccasions = selectedAllocations[key] || [];
          const occasionIds = resolveOccasionIdsForInviteLog(allocatedOccasions);

          if (occasionIds.length > 0) {
            const existingKeys = (attendee.invitations || []).map(
              (inv) =>
                `${inv.type}-${inv.inviteKind || "invitation"}-${
                  inv.occasionId?.toString?.() || inv.occasionId
                }`
            );

            const missingOccasions = occasionIds.filter(
              (occId) =>
                !existingKeys.includes(
                  `${formData.invitationType}-${inviteKind || "invitation"}-${occId}`
                )
            );

            if (missingOccasions.length > 0) {
              payloads.push({
                eventId: selectedEvent?.eventId,
                guestId: mainGuest?._id || attendee.mainGuestId,
                attendeeId: attendee._id,
                invitedOccasionIds: missingOccasions.filter((id) =>
                  /^[a-fA-F0-9]{24}$/.test(String(id))
                ),
                invitations: buildInvitations(missingOccasions, attendee._id),
                demographic: attendee.demographic || "",
                gender: attendee.gender || "",
                foodPreference: attendee.foodPreference || "",
                allergic: attendee.allergic || "",
                specialNeeds: attendee.specialNeeds || "",
                specialRequest: attendee.specialRequest || "",
                enteredBy: eventEntity?.username || "admin",
                modifiedBy: eventEntity?.username || "admin",
              });
            }
          }
        });
      });

      if (payloads.length === 0) {
        if (!quiet) toast.error("No new invites to send.");
        return;
      }

      console.log("Final payloads being sent:", payloads);

      await Promise.all(
        payloads.map((body) => axios.post(sendInvites(), body))
      );

      if (!quiet) toast.success("Invites sent successfully!");
      await fetchSavedInvites();
    } catch (error) {
      console.error("Error sending invites:", error);
      if (!quiet) toast.error("Failed to send invites.");
    }
  };

  const handleSendEmailAndInvites = async ({
    sendAllOccasions = false,
  } = {}) => {
    if (emailSending) return;
    setEmailSending(true);
    try {
      const result = await handleSendEmail({ sendAllOccasions });
      if (!result) return;

      const { successCount, failedCount } = result;

      if (successCount > 0) {
        await handleSendInvites(
          {},
          {
            forceAllOccasions: sendAllOccasions,
            selectedOccasionId:
              !sendAllOccasions && previewMailOccasionId
                ? previewMailOccasionId
                : null,
            quiet: true,
          }
        );
      }

      if (successCount > 0 && failedCount === 0) {
        toast.success(
          sendAllOccasions
            ? `All done — ${successCount} email(s) sent successfully.`
            : `${successCount} email(s) sent for the selected occasion.`
        );
      } else if (successCount > 0 && failedCount > 0) {
        toast.error(
          `Sent ${successCount} email(s), failed ${failedCount}. Check recipients and try again for failures.`
        );
      } else {
        toast.error(
          `Failed to send emails (${failedCount} failure${
            failedCount === 1 ? "" : "s"
          }).`
        );
      }
    } catch (err) {
      console.error("Error sending emails or invites:", err);
      toast.error("Failed to send emails or save invites.");
    } finally {
      setEmailSending(false);
    }
  };

  const handleUpdate = async (doc, invitation) => {
    //  Always use inviteDocId from SendInvites collection
    const docId = doc.inviteDocId;
    const invitationId = invitation._id;
    const key = `${docId}-${invitationId}`;

    const updatedData = editableInvites[key];

    if (!updatedData) {
      toast.error("No changes made.");
      return;
    }

    try {
      await axios.put(updateSentInvite(docId, invitationId), updatedData);
      toast.success("Invite updated successfully!");
      await fetchSavedInvites(); // refresh table
      setEditableInvites((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update invite.");
    }
  };

  const handleInviteChange = (docId, invitationId, field, value) => {
    const key = `${docId}-${invitationId}`;
    setEditableInvites((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

const handleExportToExcel = () => {
  if (!occasions.length || !guestWithAttendees.length) {
    toast.error("No data available for export.");
    return;
  }

  const workbook = XLSX.utils.book_new();

  occasions.forEach((occasion) => {
    const sheetData = [];
    const invitationTypes = new Set();

    // Detect invitation types for allocated guests/attendees
    guestWithAttendees.forEach((entry) => {
      const mainGuest = entry.guest;
      const attendees = entry.attendees || [];

      const mainKey = `${mainGuest._id}-null`;
      const allocatedOccasionsMain = selectedAllocations[mainKey] || [];
      const isMainAllocated = allocatedOccasionsMain.includes(occasion._id);

      if (isMainAllocated) {
        const inviteRecord = savedInvites.find(inv => inv.guestId === mainGuest._id && !inv.attendeeId);
        inviteRecord?.invitations
          .filter(i => i.occasionId === occasion._id)
          .forEach(i => invitationTypes.add(i.type));
      }

      attendees.forEach((att) => {
        const key = `${mainGuest._id}-${att._id}`;
        const allocatedOccasionsAtt = selectedAllocations[key] || [];
        const isAttAllocated = allocatedOccasionsAtt.includes(occasion._id);
        if (isAttAllocated) {
          const inviteRecord = savedInvites.find(inv => inv.guestId === mainGuest._id && inv.attendeeId === att._id);
          inviteRecord?.invitations
            .filter(i => i.occasionId === occasion._id)
            .forEach(i => invitationTypes.add(i.type));
        }
      });
    });

    // Build header
    const header = ["Record Type", "Guest Name", "Gender", "Relationship", "Attendee Type", "Email", "Mobile No"];
    invitationTypes.forEach((type) => {
      header.push(`${type} Sent Date`);
      header.push(`${type} Received Date`);
      header.push(`${type} Status`);
      header.push(`${type} Remarks`);
    });
    sheetData.push(header);

    // Totals row
    let totalGuests = 0;
    let totalAttendees = 0;
    guestWithAttendees.forEach(entry => {
      const mainGuest = entry.guest;
      const attendees = entry.attendees || [];
      if ((selectedAllocations[`${mainGuest._id}-null`] || []).includes(occasion._id)) totalGuests++;
      attendees.forEach(att => {
        if ((selectedAllocations[`${mainGuest._id}-${att._id}`] || []).includes(occasion._id)) totalAttendees++;
      });
    });
    sheetData.push([
      totalGuests + totalAttendees,
      `Guests: ${totalGuests}, Attendees: ${totalAttendees}`,
      "", "", "", "", "",
      ...Array(invitationTypes.size * 4).fill(""),
    ]);

    // Fill data
    const rowColors = [];
    guestWithAttendees.forEach(entry => {
      const mainGuest = entry.guest;
      const attendees = entry.attendees || [];
      const allPersons = [];

      if ((selectedAllocations[`${mainGuest._id}-null`] || []).includes(occasion._id)) {
        allPersons.push({ ...mainGuest, role: "Guest" });
      }

      attendees.forEach(att => {
        if ((selectedAllocations[`${mainGuest._id}-${att._id}`] || []).includes(occasion._id)) {
          allPersons.push({ ...att, role: "Attendee", mainGuestId: mainGuest._id });
        }
      });

      allPersons.forEach(person => {
        const row = [
          person.role,
          getFullName(person),
          person.gender || "",
          person.relationWithMainMember || "",
          person.demographic || "",
          person.email || "",
          person.mobileNo || "",
        ];

        const inviteRecord = savedInvites.find(inv => {
          if (person.role === "Guest") return inv.guestId === person._id && !inv.attendeeId;
          return inv.guestId === person.mainGuestId && inv.attendeeId === person._id;
        });

        const invitesForOccasion = inviteRecord?.invitations.filter(i => i.occasionId === occasion._id);

        invitationTypes.forEach(type => {
          const inv = invitesForOccasion?.find(i => i.type === type);
          row.push(inv?.sendDate ? new Date(inv.sendDate).toLocaleDateString() : "");
          row.push(inv?.receivedDate ? new Date(inv.receivedDate).toLocaleDateString() : "");
          row.push(inv?.status || "");
          row.push(inv?.remarks || "");
        });

        sheetData.push(row);
        rowColors.push(person.role === "Guest" ? "C7E2E3" : "ECF5F5");
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Calculate dynamic column widths based on max content length
    const colWidths = header.map((_, colIndex) => {
      let maxLength = header[colIndex].length;
      for (let rowIndex = 1; rowIndex < sheetData.length; rowIndex++) {
        const cellValue = sheetData[rowIndex][colIndex] ? sheetData[rowIndex][colIndex].toString() : "";
        maxLength = Math.max(maxLength, cellValue.length);
      }
      return { wch: Math.min(maxLength + 5, 50) }; // limit max width
    });
    worksheet["!cols"] = colWidths;

    // Style header and totals
    const typeColors = ["B3FFD4", "B3FFF6", "B3E3FF", "B3C2FF"];
    for (let r = 0; r <= 1; r++) {
      for (let c = 0; c < header.length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[cellRef]) continue;
        if (c <= 6) {
          worksheet[cellRef].s = { fill: { fgColor: { rgb: "19B0B0" } }, font: { bold: true }, alignment: { horizontal: "left" } };
        } else {
          const typeIndex = Math.floor((c - 7) / 4);
          const color = typeColors[typeIndex % typeColors.length];
          worksheet[cellRef].s = { fill: { fgColor: { rgb: color } }, font: { bold: true }, alignment: { horizontal: "left" } };
        }
      }
    }

    // Apply guest/attendee row colors
    for (let r = 2; r < sheetData.length; r++) {
      const fillColor = rowColors[r - 2];
      for (let c = 0; c < header.length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[cellRef]) continue;
        worksheet[cellRef].s = worksheet[cellRef].s || {};
        worksheet[cellRef].s.fill = { fgColor: { rgb: fillColor } };
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, occasion.OccasionName);
  });

  XLSX.writeFile(workbook, `Event_Invites_${selectedEvent?.eventName || "Export"}.xlsx`);
  toast.success("Exported to Excel successfully!");
};




  if (!inviteKind) {
    return (
      <div className="container-fluid AllowAttendee send-invites-page">
        <div className="occasion-top send-invites-page-title d-flex justify-content-between align-items-center">
          <h2 className="mb-0">Invites</h2>
        </div>

        <div className="border rounded shadow bg-white py-4 px-4">
          <p className="send-invite-kind-intro mb-4">
            Choose which type of invite you want to send.
          </p>
          <div className="row g-3 send-invite-kind-grid">
            {INVITE_KINDS.map((kind) => (
              <div key={kind.id} className="col-12 col-md-6 col-xl">
                <button
                  type="button"
                  className="send-invite-kind-card"
                  onClick={() => handleSelectInviteKind(kind.id)}
                >
                  <span className="send-invite-kind-icon">
                    <i className={`bi ${kind.icon}`} aria-hidden="true" />
                  </span>
                  <span className="send-invite-kind-title">{kind.title}</span>
                  <span className="send-invite-kind-subtitle">
                    {kind.subtitle}
                  </span>
                  <span className="send-invite-kind-detail">{kind.detail}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activeKindMeta =
    INVITE_KINDS.find((k) => k.id === inviteKind) || INVITE_KINDS[0];

  return (
    <div className="container-fluid AllowAttendee send-invites-page">
      <div className="occasion-top send-invites-page-title d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <button
            type="button"
            className="btn send-invites-btn send-invites-btn-outline"
            onClick={handleBackToInviteKinds}
          >
            ← All invite types
          </button>
          <h2 className="mb-0">{activeKindMeta.title}</h2>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="border rounded shadow bg-white py-4 px-4">
           

          <div className="row align-items-center">
            <div className="col-xl-8">
              <div className="row">
              {needsOccasion && (
              <div className="col-md-4 mb-3">
                <label className="form-label">Event Occasion</label>
                <select
                  className="form-select"
                  name="occasionId"
                  value={formData.occasionId}
                  onChange={(e) => {
                    const selectedOccasionId = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      occasionId: selectedOccasionId,
                      invitationType: "",
                    }));
                    syncInvitationDropdown(selectedOccasionId);
                  }}
                >
                  <option value="all">All</option>
                  {occasions?.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.OccasionName}
                    </option>
                  ))}
                </select>
              </div>
              )}

              <div className="col-md-4 mb-3">
                <label className="form-label">Invitation Type</label>
                <select
                  className="form-select"
                  name="invitationType"
                  value={formData.invitationType}
                  onChange={handleChange}
                >
                  <option value="">Select Invitation Type</option>
                  {invitationDropdown.map((item) => (
                    <option key={item._id} value={item.Lookup_Value_Code}>
                      {item.Lookup_Value_Name}
                    </option>
                  ))}
                </select>
              </div>
              
              {canEdit && (
              <div className="col-md-4">
                <label className="form-label">&nbsp;</label><br />
                {(formData.occasionId || !needsOccasion) && (
                  <>
                    {formData.invitationType === "Email" ||
                    isWhatsAppInvitationType(formData.invitationType) ? (
                      <button
                        type="button"
                        className="btn send-invites-btn"
                        onClick={
                          isWhatsAppInvitationType(formData.invitationType)
                            ? handleWhatsAppPreviewClick
                            : handlePreviewClick
                        }
                      >
                        Preview
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn send-invites-btn"
                        onClick={handleSendInvites}
                        disabled={!formData.invitationType}
                      >
                        Send
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            </div>

            
            </div>
            <div className="col-xl-4 d-flex justify-content-end align-items-end gap-2 flex-wrap mb-3">
              <div className="send-invites-table-search">
                <label className="form-label" htmlFor="send-invites-search">
                  Search
                </label>
                <input
                  id="send-invites-search"
                  type="search"
                  className="form-control"
                  placeholder="Search guest or attendee…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {inviteKind === "invitation" && (
              <div className="send-invites-rsvp-wrap">
                <label className="form-label d-none d-xl-block">&nbsp;</label>
                <button
                  type="button"
                  className="btn send-invites-btn send-invites-btn-outline"
                  onClick={() => navigate("/track-invites")}
                >
                  + RSVP
                </button>
              </div>
              )}
            </div>
          </div>

         


            

            <hr />

            {hasNoAssignments && (
              <div className="send-invites-empty-assignment mb-4">
                <h5 className="mb-2">{emptyAssignmentCopy.title}</h5>
                <p className="mb-3">{emptyAssignmentCopy.body}</p>
                <button
                  type="button"
                  className="btn send-invites-btn"
                  onClick={() => navigate(emptyAssignmentCopy.actionPath)}
                >
                  {emptyAssignmentCopy.actionLabel}
                </button>
              </div>
            )}

            <div className="table-responsive mb-4 rsvp-table">
              {needsOccasion && !formData.occasionId ? (
                <div className="mt-4">
                  <p>
                    Please Select <b>Event Occasion</b> and{" "}
                    <b>Invitation Type</b>
                  </p>
                </div>
              ) : null}
              {(formData.occasionId || !needsOccasion) &&
                !hasNoAssignments &&
                filteredGuestWithAttendees.length === 0 && (
                  <div className="send-invites-empty-assignment mb-3">
                    <h5 className="mb-2">No matching guests</h5>
                    <p className="mb-0">
                      Guests need to be allocated to an occasion in Guest
                      Allocation
                      {inviteKind !== "invitation"
                        ? `, and also have ${inviteKind} assigned,`
                        : ""}{" "}
                      to appear here.
                    </p>
                  </div>
                )}

              {(formData.occasionId || !needsOccasion) && !hasNoAssignments && (
                <table className="table table-bordered align-middle table-hover">
                  <thead className="thead-dark table-bg-new">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            Object.keys(selectedRows).length > 0 &&
                            Object.values(selectedRows).every(
                              (val) => val === true
                            )
                          }
                          onChange={(e) => {
                            const newState = {};
                            Object.keys(selectedRows).forEach((key) => {
                              newState[key] = e.target.checked;
                            });
                            setSelectedRows(newState);
                          }}
                        />
                      </th>

                      <th>Sr No.</th>
                      <th>Guest Name</th>

                      {/* 👇 Show Invitation + Action columns only if not "all" */}
                      {formData.occasionId !== "all" && (
                        <>
                          <th colSpan="3">Invitation</th>
                          <th></th>
                          {canEdit && <th>Action</th>}
                        </>
                      )}
                    </tr>

                    {/* 👇 Sub-header row only if not "all" */}
                    {formData.occasionId !== "all" && (
                      <tr>
                        <th>
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-sm text-gray-600">
                              {
                                filteredGuestWithAttendees.flatMap((entry) => {
                                  const arr = [];
                                  if (
                                    entry.guest &&
                                    selectedRows[`guest-${entry.guest._id}`]
                                  ) {
                                    arr.push(entry.guest._id);
                                  }
                                  entry.attendees?.forEach((att) => {
                                    if (selectedRows[`attendee-${att._id}`])
                                      arr.push(att._id);
                                  });
                                  return arr;
                                }).length
                              }
                            </span>
                          </div>
                        </th>
                        <th>
                          <span className="text-sm text-gray-500">
                            ({TotalGuestCount})
                          </span>
                        </th>
                        <th>
                          <span className="text-sm text-gray-500">
                            (Guests: {totalGuests}, Attendees: {totalAttendees})
                          </span>
                        </th>
                        <th>Sent Date</th>
                        <th>Received Date</th>
                        <th>Status</th>
                        <th>Remarks</th>
                        {canEdit && <th></th>}{" "}
                      </tr>
                    )}
                  </thead>

                  <tbody>
                    {Array.isArray(filteredGuestWithAttendees) &&
                      filteredGuestWithAttendees.map((entry, index) => {
                        const mainGuest = entry.guest;
                        const attendees = entry.attendees || [];

                        if (!mainGuest && attendees.length === 0) return null;

                        return (
                          <React.Fragment key={mainGuest?.inviteDocId || index}>
                            {/* Main Guest Row */}
                            {mainGuest && (
                              <tr className="table-primary">
                                {/* Checkbox */}
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={
                                      selectedRows[`guest-${mainGuest._id}`] ||
                                      false
                                    }
                                    onChange={() =>
                                      handleCheckboxChange(
                                        `guest-${mainGuest._id}`
                                      )
                                    }
                                  />
                                </td>

                                {/* Sr No */}
                                <td>{index + 1}</td>

                                {/* Guest Name */}
                                <td className="text-start">
                                  {getFullName(mainGuest)}
                                </td>

                                {/* Only render if not "all" */}
                                {formData.occasionId !== "all" && (
                                  <>
                                    {/* Send Date */}
                                    <td>
                                      {editMode[`guest-${mainGuest._id}`] ? (
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${mainGuest.inviteDocId}-${mainGuest.currentInvitation?._id}`
                                            ]?.sendDate ||
                                            (mainGuest.currentInvitation
                                              ?.sendDate
                                              ? new Date(
                                                  mainGuest.currentInvitation.sendDate
                                                )
                                                  .toISOString()
                                                  .split("T")[0]
                                              : "")
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              mainGuest.inviteDocId,
                                              mainGuest.currentInvitation?._id,
                                              "sendDate",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : mainGuest.currentInvitation
                                          ?.sendDate ? (
                                        new Date(
                                          mainGuest.currentInvitation.sendDate
                                        ).toLocaleDateString()
                                      ) : (
                                        ""
                                      )}
                                    </td>

                                    {/* Received Date */}
                                    <td>
                                      {editMode[`guest-${mainGuest._id}`] ? (
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${mainGuest.inviteDocId}-${mainGuest.currentInvitation?._id}`
                                            ]?.receivedDate ||
                                            (mainGuest.currentInvitation
                                              ?.receivedDate
                                              ? new Date(
                                                  mainGuest.currentInvitation.receivedDate
                                                )
                                                  .toISOString()
                                                  .split("T")[0]
                                              : "")
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              mainGuest.inviteDocId,
                                              mainGuest.currentInvitation?._id,
                                              "receivedDate",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : mainGuest.currentInvitation
                                          ?.receivedDate ? (
                                        new Date(
                                          mainGuest.currentInvitation.receivedDate
                                        ).toLocaleDateString()
                                      ) : (
                                        ""
                                      )}
                                    </td>

                                    {/* Status */}
                                    <td>
                                      {editMode[`guest-${mainGuest._id}`] ? (
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${mainGuest.inviteDocId}-${mainGuest.currentInvitation?._id}`
                                            ]?.status ||
                                            mainGuest.currentInvitation
                                              ?.status ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              mainGuest.inviteDocId,
                                              mainGuest.currentInvitation?._id,
                                              "status",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : (
                                        mainGuest.currentInvitation?.status ||
                                        ""
                                      )}
                                    </td>

                                    {/* Remarks */}
                                    <td>
                                      {editMode[`guest-${mainGuest._id}`] ? (
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${mainGuest.inviteDocId}-${mainGuest.currentInvitation?._id}`
                                            ]?.remarks ||
                                            mainGuest.currentInvitation
                                              ?.remarks ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              mainGuest.inviteDocId,
                                              mainGuest.currentInvitation?._id,
                                              "remarks",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : (
                                        mainGuest.currentInvitation?.remarks ||
                                        ""
                                      )}
                                    </td>

                                    {/* Edit / Update Button */}
                                    {/* Edit / Update Button for Main Guest */}
                         {canEdit && (
  <td className="text-center">
    {mainGuest.currentInvitation ? (
      !editMode[`guest-${mainGuest._id}`] ? (
        <button
          className="btn edit-btn"
          onClick={() =>
            setEditMode((prev) => ({
              ...prev,
              [`guest-${mainGuest._id}`]: true,
            }))
          }
        >
          Edit
        </button>
      ) : (
        <button
          className="btn btn-enable"
          onClick={() => {
            handleUpdate(mainGuest, mainGuest.currentInvitation);
            setEditMode((prev) => ({
              ...prev,
              [`guest-${mainGuest._id}`]: false,
            }));
          }}
        >
          Update
        </button>
      )
    ) : (
      <span>-</span> // Show dash if no invitation exists
    )}
  </td>
)}

                                  </>
                                )}
                              </tr>
                            )}

                            {/* Attendee Rows */}
                            {attendees.map((attendee, subIndex) => (
                              <tr
                                key={
                                  attendee.inviteDocId || `attendee-${subIndex}`
                                }
                                className="table-secondary text-sm"
                              >
                                {/* Checkbox */}
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={
                                      selectedRows[
                                        `attendee-${attendee._id}`
                                      ] || false
                                    }
                                    onChange={() =>
                                      handleCheckboxChange(
                                        `attendee-${attendee._id}`
                                      )
                                    }
                                  />
                                </td>

                                {/* Sr No */}
                                <td>{`${index + 1}.${subIndex + 1}`}</td>

                                {/* Attendee Name */}
                                <td className="text-start pl-4">
                                  {getFullName(attendee)}{" "}
                                  <span className="text-muted">
                                    (
                                    {attendee.relationWithMainMember ||
                                      "Attendee"}
                                    )
                                  </span>
                                </td>

                                {/* Only render if not "all" */}
                                {formData.occasionId !== "all" && (
                                  <>
                                    {/* Send Date */}
                                    <td>
                                      {editMode[`attendee-${attendee._id}`] ? (
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${attendee.inviteDocId}-${attendee.currentInvitation?._id}`
                                            ]?.sendDate ||
                                            (attendee.currentInvitation
                                              ?.sendDate
                                              ? new Date(
                                                  attendee.currentInvitation.sendDate
                                                )
                                                  .toISOString()
                                                  .split("T")[0]
                                              : "")
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              attendee.inviteDocId,
                                              attendee.currentInvitation?._id,
                                              "sendDate",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : attendee.currentInvitation
                                          ?.sendDate ? (
                                        new Date(
                                          attendee.currentInvitation.sendDate
                                        ).toLocaleDateString()
                                      ) : (
                                        ""
                                      )}
                                    </td>

                                    {/* Received Date */}
                                    <td>
                                      {editMode[`attendee-${attendee._id}`] ? (
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${attendee.inviteDocId}-${attendee.currentInvitation?._id}`
                                            ]?.receivedDate ||
                                            (attendee.currentInvitation
                                              ?.receivedDate
                                              ? new Date(
                                                  attendee.currentInvitation.receivedDate
                                                )
                                                  .toISOString()
                                                  .split("T")[0]
                                              : "")
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              attendee.inviteDocId,
                                              attendee.currentInvitation?._id,
                                              "receivedDate",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : attendee.currentInvitation
                                          ?.receivedDate ? (
                                        new Date(
                                          attendee.currentInvitation.receivedDate
                                        ).toLocaleDateString()
                                      ) : (
                                        ""
                                      )}
                                    </td>

                                    {/* Status */}
                                    <td>
                                      {editMode[`attendee-${attendee._id}`] ? (
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${attendee.inviteDocId}-${attendee.currentInvitation?._id}`
                                            ]?.status ||
                                            attendee.currentInvitation
                                              ?.status ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              attendee.inviteDocId,
                                              attendee.currentInvitation?._id,
                                              "status",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : (
                                        attendee.currentInvitation?.status || ""
                                      )}
                                    </td>

                                    {/* Remarks */}
                                    <td>
                                      {editMode[`attendee-${attendee._id}`] ? (
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={
                                            editableInvites[
                                              `${attendee.inviteDocId}-${attendee.currentInvitation?._id}`
                                            ]?.remarks ||
                                            attendee.currentInvitation
                                              ?.remarks ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleInviteChange(
                                              attendee.inviteDocId,
                                              attendee.currentInvitation?._id,
                                              "remarks",
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : (
                                        attendee.currentInvitation?.remarks ||
                                        ""
                                      )}
                                    </td>

                                    {/* Edit / Update Button */}
                                    {/* Edit / Update Button */}
                                    
                                 {canEdit && (
  <td className="text-center">
    {attendee.currentInvitation ? (
      !editMode[`attendee-${attendee._id}`] ? (
        <button
          className="btn edit-btn"
          onClick={() =>
            setEditMode((prev) => ({
              ...prev,
              [`attendee-${attendee._id}`]: true,
            }))
          }
        >
          Edit
        </button>
      ) : (
        <button
          className="btn btn-enable"
          onClick={() => {
            handleUpdate(attendee, attendee.currentInvitation);
            setEditMode((prev) => ({
              ...prev,
              [`attendee-${attendee._id}`]: false,
            }));
          }}
        >
          Update
        </button>
      )
    ) : (
      <span>-</span> // Show dash if no invitation exists
    )}
  </td>
)}

                                  </>
                                )}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>

            <hr />
            {canEdit && (
              <div className="mt-4 d-flex justify-content-end gap-2 justify-content-center py-3">
                {/* <button
                type="submit"
                className="btn btn-success btn-save"
                // onClick={handleSubmit}
              >
                Save
              </button> */}
                {formData.occasionId && (
                  <button
                    type="reset"
                    className="btn excel-btn pd10"
                    onClick={handleExportToExcel}
                  >
                    Export to Excel <i className="bi bi-download"></i>
                  </button>
                )}
              </div>
            )}
            {whatsappModalOpen &&
              createPortal(
                <div
                  className="modal fade show d-block send-invites-whatsapp-modal"
                  tabIndex="-1"
                  role="dialog"
                  aria-modal="true"
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.55)" }}
                >
                  <div className="modal-dialog modal-dialog-centered send-invites-whatsapp-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Preview WhatsApp</h5>
                        <button
                          type="button"
                          className="btn-close"
                          aria-label="Close"
                          onClick={() => setWhatsappModalOpen(false)}
                        ></button>
                      </div>
                      <div className="modal-body">
                        {(splitInviteByOccasion ||
                          (inviteKind === "invitation" &&
                            formData.occasionId &&
                            formData.occasionId !== "all")) && (
                          <div className="send-invites-preview-occasion-bar mb-3">
                            {splitInviteByOccasion ? (
                              previewWhatsAppOccasions.length > 0 ? (
                                <div className="send-invites-preview-occasion-tabs">
                                  {previewWhatsAppOccasions.map((occasion) => {
                                    const theme = getOccasionThemeForId(
                                      occasions,
                                      occasion._id
                                    );
                                    const isActive =
                                      String(previewWhatsAppOccasionId) ===
                                      String(occasion._id);
                                    return (
                                      <button
                                        key={occasion._id}
                                        type="button"
                                        className={`send-invites-preview-occasion-tab${
                                          isActive ? " is-active" : ""
                                        }`}
                                        style={{
                                          borderColor: theme.primary,
                                          backgroundColor: isActive
                                            ? theme.primary
                                            : theme.soft,
                                          color: isActive
                                            ? "#fff"
                                            : theme.primary,
                                        }}
                                        onClick={() =>
                                          setPreviewWhatsAppOccasionId(
                                            String(occasion._id)
                                          )
                                        }
                                      >
                                        {occasion.OccasionName}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-muted mb-0 text-center">
                                  No allocated occasions for the selected
                                  preview guest.
                                </p>
                              )
                            ) : (
                              <div className="send-invites-preview-occasion-tabs">
                                {(() => {
                                  const occasion = occasions.find(
                                    (o) =>
                                      String(o._id) ===
                                      String(formData.occasionId)
                                  );
                                  const theme = getOccasionThemeForId(
                                    occasions,
                                    formData.occasionId
                                  );
                                  return (
                                    <span
                                      className="send-invites-preview-occasion-tab is-active"
                                      style={{
                                        borderColor: theme.primary,
                                        backgroundColor: theme.primary,
                                        color: "#fff",
                                      }}
                                    >
                                      {occasion?.OccasionName || "Occasion"}
                                    </span>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                        {(whatsappPreviewData?.previews || []).length === 0 ? (
                          <div className="alert alert-warning mb-0">
                            Unable to build WhatsApp preview for this invite.
                          </div>
                        ) : (
                          <div className="whatsapp-preview-stage-outer">
                            <div className="whatsapp-preview-stage-wrap">
                              {whatsappPreviewGuests.length > 1 ? (
                                <button
                                  type="button"
                                  className="whatsapp-preview-nav-btn"
                                  aria-label="Previous guest preview"
                                  disabled={whatsappPreviewGuestIndex <= 0}
                                  onClick={() =>
                                    setWhatsappPreviewGuestIndex((i) =>
                                      Math.max(0, i - 1)
                                    )
                                  }
                                >
                                  ‹
                                </button>
                              ) : (
                                <span className="whatsapp-preview-nav-spacer" />
                              )}

                              <div className="whatsapp-preview-phones">
                                {(whatsappPreviewData?.previews || [])
                                  .slice(0, 1)
                                  .map((preview, idx) => {
                                    const bubbles =
                                      Array.isArray(preview.bubbles) &&
                                      preview.bubbles.length
                                        ? preview.bubbles
                                        : [
                                            {
                                              bodyText: preview.bodyText,
                                              headerMediaUrl:
                                                preview.headerMediaUrl,
                                              buttons: preview.buttons,
                                            },
                                          ];
                                    return (
                                      <div
                                        key={`${
                                          preview.templateName || "wa"
                                        }-${
                                          whatsappPreviewGuest?.id ||
                                          whatsappPreviewGuest?.regNo ||
                                          "guest"
                                        }-${idx}`}
                                        className="whatsapp-preview-block"
                                      >
                                        <div className="whatsapp-phone-stage">
                                          <div className="whatsapp-phone">
                                            <div className="whatsapp-phone-notch" />
                                            <div className="whatsapp-phone-screen">
                                              <div className="whatsapp-chat-header">
                                                <span className="whatsapp-chat-avatar">
                                                  F
                                                </span>
                                                <div>
                                                  <strong>Festiva</strong>
                                                  <em>WhatsApp Business</em>
                                                </div>
                                              </div>
                                              <div className="whatsapp-chat-body">
                                                {bubbles.map(
                                                  (bubble, bubbleIdx) => {
                                                    const mediaUrl =
                                                      inviteKind ===
                                                      "invitation"
                                                        ? whatsappEventPassUrl
                                                        : bubble.headerMediaUrl ||
                                                          (bubbleIdx === 0
                                                            ? preview.headerMediaUrl
                                                            : "");
                                                    const showInvitePassSlot =
                                                      inviteKind ===
                                                        "invitation" &&
                                                      bubbleIdx === 0;
                                                    return (
                                                    <div
                                                      key={`bubble-${
                                                        bubble.tripType ||
                                                        bubbleIdx
                                                      }`}
                                                      className="whatsapp-bubble"
                                                    >
                                                      {showInvitePassSlot ? (
                                                        <div className="whatsapp-bubble-media-wrap">
                                                          {mediaUrl ? (
                                                            <img
                                                              src={mediaUrl}
                                                              alt="Event pass"
                                                              className="whatsapp-bubble-media"
                                                            />
                                                          ) : (
                                                            <div className="whatsapp-bubble-media-skeleton">
                                                              Preparing event
                                                              pass…
                                                            </div>
                                                          )}
                                                        </div>
                                                      ) : mediaUrl ? (
                                                        <img
                                                          src={mediaUrl}
                                                          alt=""
                                                          className="whatsapp-bubble-media"
                                                        />
                                                      ) : null}
                                                      <div className="whatsapp-bubble-text">
                                                        {bubble.bodyText ||
                                                          "Message preview unavailable."}
                                                      </div>
                                                      {(
                                                        bubble.buttons ||
                                                        (bubbleIdx === 0
                                                          ? preview.buttons
                                                          : []) ||
                                                        []
                                                      ).length > 0 && (
                                                        <div className="whatsapp-bubble-actions">
                                                          {(
                                                            bubble.buttons ||
                                                            preview.buttons ||
                                                            []
                                                          ).map((btn) => (
                                                            <span
                                                              key={`${btn.label}-${btn.url}`}
                                                              className="whatsapp-action-btn"
                                                            >
                                                              {btn.label}
                                                            </span>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </div>
                                                    );
                                                  }
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>

                              {whatsappPreviewGuests.length > 1 ? (
                                <button
                                  type="button"
                                  className="whatsapp-preview-nav-btn"
                                  aria-label="Next guest preview"
                                  disabled={
                                    whatsappPreviewGuestIndex >=
                                    whatsappPreviewGuests.length - 1
                                  }
                                  onClick={() =>
                                    setWhatsappPreviewGuestIndex((i) =>
                                      Math.min(
                                        whatsappPreviewGuests.length - 1,
                                        i + 1
                                      )
                                    )
                                  }
                                >
                                  ›
                                </button>
                              ) : (
                                <span className="whatsapp-preview-nav-spacer" />
                              )}
                            </div>
                            {whatsappPreviewGuests.length > 1 ? (
                              <div className="whatsapp-preview-guest-count">
                                {whatsappPreviewGuestIndex + 1} /{" "}
                                {whatsappPreviewGuests.length}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                      <div className="modal-footer send-invites-whatsapp-footer">
                        <button
                          type="button"
                          className="btn send-invites-whatsapp-cancel-btn"
                          disabled={whatsappSending}
                          onClick={() => setWhatsappModalOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn send-invites-whatsapp-send-btn is-occasion-themed"
                          style={{
                            "--occasion-primary": whatsappActiveTheme.primary,
                          }}
                          disabled={whatsappSending}
                          onClick={() =>
                            handleSendWhatsAppAndInvites({
                              sendAllOccasions: false,
                            })
                          }
                        >
                          {whatsappSending
                            ? "Sending WhatsApp…"
                            : inviteKind === "invitation" &&
                                splitInviteByOccasion &&
                                previewWhatsAppOccasionId
                              ? `Send (${
                                  occasions.find(
                                    (o) =>
                                      String(o._id) ===
                                      String(previewWhatsAppOccasionId)
                                  )?.OccasionName || "Selected"
                                })`
                              : "Send WhatsApp"}
                        </button>
                        {inviteKind === "invitation" && (
                          <button
                            type="button"
                            className="btn send-invites-whatsapp-cancel-btn"
                            disabled={whatsappSending}
                            onClick={() =>
                              handleSendWhatsAppAndInvites({
                                sendAllOccasions: true,
                              })
                            }
                          >
                            {whatsappSending
                              ? "Sending WhatsApp…"
                              : "Send for All Occasions"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>,
                document.body
              )}
            {emailModal.isOpen && (
              <div
                className="modal fade show d-block send-invites-email-modal"
                tabIndex="-1"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <div className="modal-dialog modal-dialog-centered send-invites-email-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        Preview Email
                        {splitInviteByOccasion && previewOccasionMails.length > 0
                          ? ` (${previewOccasionMails.findIndex(
                              (o) =>
                                String(o._id) === String(previewMailOccasionId)
                            ) + 1}/${previewOccasionMails.length})`
                          : ""}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={closeEmailModal}
                      ></button>
                    </div>

                    <div className="modal-body">
                      {(splitInviteByOccasion ||
                        (formData.occasionId &&
                          formData.occasionId !== "all")) && (
                        <div className="send-invites-preview-occasion-bar mb-3">
                          {splitInviteByOccasion ? (
                            previewOccasionMails.length > 0 ? (
                              <div className="send-invites-preview-occasion-tabs">
                                {previewOccasionMails.map((occasion) => {
                                  const theme = getOccasionThemeForId(
                                    occasions,
                                    occasion._id
                                  );
                                  const isActive =
                                    String(previewMailOccasionId) ===
                                    String(occasion._id);
                                  return (
                                    <button
                                      key={occasion._id}
                                      type="button"
                                      className={`send-invites-preview-occasion-tab${
                                        isActive ? " is-active" : ""
                                      }`}
                                      style={{
                                        borderColor: theme.primary,
                                        backgroundColor: isActive
                                          ? theme.primary
                                          : theme.soft,
                                        color: isActive
                                          ? "#fff"
                                          : theme.primary,
                                      }}
                                      onClick={() => {
                                        emailPreviewHtmlRef.current = "";
                                        setEmailPreviewHtml("");
                                        setPreviewMailOccasionId(
                                          String(occasion._id)
                                        );
                                      }}
                                    >
                                      {occasion.OccasionName}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-muted mb-0 text-center">
                                No allocated occasions for the selected preview
                                guest.
                              </p>
                            )
                          ) : (
                            <div className="send-invites-preview-occasion-tabs">
                              {(() => {
                                const occasion = occasions.find(
                                  (o) =>
                                    String(o._id) === String(formData.occasionId)
                                );
                                const theme = getOccasionThemeForId(
                                  occasions,
                                  formData.occasionId
                                );
                                return (
                                  <span
                                    className="send-invites-preview-occasion-tab is-active"
                                    style={{
                                      borderColor: theme.primary,
                                      backgroundColor: theme.primary,
                                      color: "#fff",
                                    }}
                                  >
                                    {occasion?.OccasionName || "Occasion"}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="row g-3 g-xl-4 email-preview-layout">
                        <div className="col-12 col-lg-4 email-preview-form">
                          <div className="email-preview-options">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="attachMedia"
                                checked={emailModal.attachMedia || false}
                                onChange={(e) =>
                                  setEmailModal((prev) => ({
                                    ...prev,
                                    attachMedia: e.target.checked,
                                  }))
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="attachMedia"
                              >
                                Include Media
                              </label>
                            </div>

                            {inviteKind !== "itinerary" ? (
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="addQR"
                                  checked={emailModal.addQR || false}
                                  onChange={(e) =>
                                    setEmailModal((prev) => ({
                                      ...prev,
                                      addQR: e.target.checked,
                                    }))
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="addQR"
                                >
                                  Include QR
                                </label>
                              </div>
                            ) : null}

                            <button
                              type="button"
                              className="btn btn-sm email-attach-device-btn"
                              onClick={() => emailFileInputRef.current?.click()}
                            >
                              <FaPaperclip className="me-1" />
                              Attach from device
                            </button>
                            <input
                              ref={emailFileInputRef}
                              type="file"
                              multiple
                              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                              className="d-none"
                              onChange={handleEmailDeviceFilesSelected}
                            />
                          </div>

                          {emailModal.attachMedia && (
                            <div
                              className={`email-attached-media is-visible${
                                emailPreviewOccasionMedia.length > 0
                                  ? " has-items"
                                  : ""
                              }`}
                            >
                              {emailPreviewOccasionMedia.length > 0 ? (
                                emailPreviewOccasionMedia.map((item) => (
                                  <div
                                    key={item.id || item.url}
                                    className="email-media-thumb text-center"
                                  >
                                    {item.isVideo ? (
                                      <FaVideo
                                        style={{
                                          fontSize: "40px",
                                          color: "#555",
                                        }}
                                        title="Video file"
                                      />
                                    ) : (
                                      <img
                                        src={item.url}
                                        alt={item.name || "media"}
                                        className="img-fluid"
                                        style={{
                                          maxWidth: "100px",
                                          maxHeight: "100px",
                                          objectFit: "cover",
                                          borderRadius: "8px",
                                        }}
                                      />
                                    )}
                                    <div className="email-media-thumb-name">
                                      {item.name}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="email-media-empty mb-0">
                                  No media uploaded for this occasion.
                                </p>
                              )}
                            </div>
                          )}

                          {(inviteKind === "invitation" && emailInviteCardUrl) ||
                          emailDeviceFiles.length > 0 ? (
                            <div className="email-device-files has-items">
                              {inviteKind === "invitation" &&
                              emailInviteCardUrl ? (
                                <div className="email-device-file-chip">
                                  <img
                                    src={emailInviteCardUrl}
                                    alt="Invitation card"
                                    className="email-device-file-thumb"
                                  />
                                  <span
                                    className="email-device-file-name"
                                    title="Invitation-Card.png"
                                  >
                                    Invitation-Card.png
                                  </span>
                                </div>
                              ) : null}
                              {emailDeviceFiles.map((file) => (
                                <div
                                  key={file.id}
                                  className="email-device-file-chip"
                                >
                                  {file.isImage && file.previewUrl ? (
                                    <img
                                      src={file.previewUrl}
                                      alt={file.name}
                                      className="email-device-file-thumb"
                                    />
                                  ) : file.isVideo ? (
                                    <FaVideo className="email-device-file-icon" />
                                  ) : (
                                    <FaPaperclip className="email-device-file-icon" />
                                  )}
                                  <span
                                    className="email-device-file-name"
                                    title={file.name}
                                  >
                                    {file.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="email-device-file-remove"
                                    aria-label={`Remove ${file.name}`}
                                    onClick={() =>
                                      removeEmailDeviceFile(file.id)
                                    }
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}

                          <div className="email-dynamic-fields-section mb-3">
                            <div className="email-dynamic-fields-header">
                              Insert dynamic field
                            </div>
                            <select
                              className="form-select email-dynamic-fields-select"
                              defaultValue=""
                              disabled={!availableDynamicFields.length}
                              onChange={(e) => {
                                const token = e.target.value;
                                if (token) {
                                  insertDynamicField(token);
                                  e.target.value = "";
                                }
                              }}
                            >
                              <option value="" disabled>
                                {availableDynamicFields.length
                                  ? "Select a field from guest data…"
                                  : "No dynamic fields available for current guests"}
                              </option>
                              {availableDynamicFields.map((field) => (
                                <option key={field.token} value={field.token}>
                                  {field.label} ({field.token})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Subject</label>
                            <input
                              ref={subjectInputRef}
                              type="text"
                              className="form-control"
                              value={emailModal.subject}
                              onFocus={() => setDynamicFieldTarget("subject")}
                              onChange={(e) =>
                                setEmailModal((prev) => ({
                                  ...prev,
                                  subject: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Content</label>
                            <textarea
                              ref={contentInputRef}
                              className="form-control email-preview-content"
                              rows="10"
                              value={emailModal.content}
                              onFocus={() => setDynamicFieldTarget("content")}
                              onChange={(e) =>
                                setEmailModal((prev) => ({
                                  ...prev,
                                  content: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Footer</label>
                            <textarea
                              className="form-control"
                              rows="2"
                              value={emailModal.footer}
                              onChange={(e) =>
                                setEmailModal((prev) => ({
                                  ...prev,
                                  footer: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="col-12 col-lg-8 email-preview-pane">
                          <div className="email-preview-toolbar">
                            <div className="email-preview-label mb-0">
                              Email preview
                            </div>
                            <div
                              className="email-preview-device-toggle"
                              role="group"
                              aria-label="Preview device"
                            >
                              <button
                                type="button"
                                className={`btn${
                                  emailPreviewDevice === "desktop"
                                    ? " is-active"
                                    : ""
                                }`}
                                onClick={() =>
                                  setEmailPreviewDevice("desktop")
                                }
                              >
                                Web
                              </button>
                              <button
                                type="button"
                                className={`btn${
                                  emailPreviewDevice === "mobile"
                                    ? " is-active"
                                    : ""
                                }`}
                                onClick={() =>
                                  setEmailPreviewDevice("mobile")
                                }
                              >
                                Mobile
                              </button>
                            </div>
                          </div>
                          {(inviteKind === "invitation" && emailInviteCardUrl) ||
                          emailDeviceFiles.length > 0 ? (
                            <div
                              className="email-preview-attachments"
                              aria-label="Email attachments"
                            >
                              {inviteKind === "invitation" &&
                              emailInviteCardUrl ? (
                                <div
                                  className="email-preview-attachment-chip"
                                  title="Invitation-Card.png"
                                >
                                  <img
                                    src={emailInviteCardUrl}
                                    alt=""
                                    className="email-preview-attachment-thumb"
                                  />
                                  <span className="email-preview-attachment-name">
                                    Invitation-Card.png
                                  </span>
                                </div>
                              ) : null}
                              {emailDeviceFiles.map((file) => (
                                <div
                                  key={`preview-${file.id}`}
                                  className="email-preview-attachment-chip"
                                  title={file.name}
                                >
                                  {file.isImage && file.previewUrl ? (
                                    <img
                                      src={file.previewUrl}
                                      alt=""
                                      className="email-preview-attachment-thumb"
                                    />
                                  ) : (
                                    <FaPaperclip
                                      className="email-preview-attachment-icon"
                                      aria-hidden="true"
                                    />
                                  )}
                                  <span className="email-preview-attachment-name">
                                    {file.name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <div
                            className={`email-preview-stage is-${emailPreviewDevice}`}
                          >
                            {emailPreviewLoading && (
                              <div className="email-preview-loading-bar" />
                            )}
                            <div
                              className={`email-preview-device is-${emailPreviewDevice}`}
                            >
                              <iframe
                                title="Email preview"
                                className="email-preview-frame"
                                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                                srcDoc={
                                  emailPreviewHtml ||
                                  "<html><body style='margin:0;background:#eef2f4'></body></html>"
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="modal-footer send-invites-email-footer">
                      <button
                        type="button"
                        className="btn send-invites-btn send-invites-btn-outline"
                        disabled={emailSending}
                        onClick={closeEmailModal}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn send-invites-btn is-occasion-themed"
                        style={{
                          "--occasion-primary": emailActiveTheme.primary,
                        }}
                        disabled={emailSending}
                        onClick={() =>
                          handleSendEmailAndInvites({ sendAllOccasions: false })
                        }
                      >
                        {emailSending
                          ? "Sending…"
                          : splitInviteByOccasion && previewMailOccasionId
                            ? `Send (${
                                occasions.find(
                                  (o) =>
                                    String(o._id) ===
                                    String(previewMailOccasionId)
                                )?.OccasionName || "Selected"
                              })`
                            : "Send"}
                      </button>
                      {(splitInviteByOccasion ||
                        inviteKind === "invitation" ||
                        inviteKind === "seat") && (
                        <button
                          type="button"
                          className="btn send-invites-btn send-invites-btn-outline"
                          disabled={emailSending}
                          onClick={() =>
                            handleSendEmailAndInvites({
                              sendAllOccasions: true,
                            })
                          }
                        >
                          {emailSending ? "Sending…" : "Send for All Occasions"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendInvites;
